import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  TemplateRef
} from '@angular/core';
import {FormBuilder, FormGroup, Validators, AbstractControl} from '@angular/forms';
import {EquineService} from '../equine.service';
import {QuoteService} from '../../quote.service';
import {ActivatedRoute, Router} from '@angular/router';
import {LocalStorageService} from 'ngx-webstorage';
import {MatSnackBar} from '@angular/material/snack-bar';
import {formatDate} from '@angular/common';
import { UserInfoService } from 'src/app/services/user-info.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ProductBasedConfigurationService } from 'src/app/product-based-configuration/product-based-configuration.service';
import { capitalizeName, capitalizeHorseName, scrollToFirstInvalidControl, getEquineDisplayPremiumData, resolveEquinePreviousPolicyId } from '../equine-utils';
import { normalizeEquineQuotedReferralState } from '../equine-premium-tbc.util';
import { environment } from 'src/environments/environment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PolicyService } from 'src/app/policy/policy.service';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-equine-quote-details',
  templateUrl: './equine-quote-details.component.html',
  styleUrls: ['./equine-quote-details.component.css', '../../quote.component.css']
})
export class EquineQuoteDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() staticData;
  @Input() modelObject: any = {};
  @Input() userDetailsLoaded = false;
  @Output() modelObjectUpdated = new EventEmitter<any>();
  @Output() GoToNextTab = new EventEmitter<void>();

  @ViewChild('aceNoSumInsuredModal') aceNoSumInsuredModalRef: TemplateRef<any>;
  @ViewChild('effectiveDateDatepicker') effectiveDateDatepicker: MatDatepicker<Date>;
  @ViewChild('postcodeAutoTrigger', { read: MatAutocompleteTrigger }) postcodeAutoTrigger: MatAutocompleteTrigger;
  private aceNoSumInsuredModalInstance: any;

  private brokersLoaded = false;
  /** When true, next ngOnChanges(modelObject) will skip patchFormValues to avoid reverting user edits (e.g. Sum Insured) and prevent request loop. Set by saveQuoteForPremiumUpdate. */
  private skipPatchFromPremiumUpdate = false;
  /** After the first patchFormValues, subsequent modelObject changes from other tabs skip patching to prevent cascading saves. */
  private initialPatchDone = false;
  private applyingCoverRules = false;
  private applyingLouReferral = false;
  private suppressAutoSave = false;
  /** True after any user-driven change that requested autosave (button setValue does not mark Angular forms dirty). */
  private formChangedByUser = false;
  /** Coalesces rapid blur/autofill autosaves into one request */
  private autoSaveSubject = new Subject<void>();
  private autoSaveSubscription: Subscription;
  private autoSaveInFlight = false;
  private pendingAutoSave = false;
  /** Debounced postcode lookup – avoids API calls on every keystroke */
  private postcodeLookupSubject = new Subject<string>();
  private postcodeLookupSubscription: Subscription;
  /** Skip lookup while applying a selected address (avoids reopening the panel). */
  private suppressPostcodeLookup = false;
  /** Bumped on select so in-flight lookups are ignored. */
  private postcodeLookupToken = 0;

  public quoteID: string = '';
  formFields: FormGroup;
  submitted = false;
  saving = false;
  isBroker = false;
  brokers: any = [];
  addresses: any[] = [];
  quote_type: number = this.route.snapshot.params.quote_type ? +this.route.snapshot.params.quote_type : 1;
  excludedPrefixes: string[] = [];
  policyReasons: any[] = [];
  public titles: string[] = ['Miss', 'Mr', 'Mrs','Ms', 'Baron', 'Baroness', 'Count', 'Countess', 'Doctor', 'Estate of', 'Executor(s) of', 'Honourable', 'Lady', 'Lord', 'Major', 'Marquis','Prince','Princess','Sir','The Duke of','The Earl of','The Honourable Lady','The Honourable Miss','The Honourable Mrs','The Honourable Sir','The Marquess of','The Right Honourable','Trustees of','Viscount','Viscountess'];

  // Horse details
  ownershipStatuses: any = [];
  yearsOfBirth: number[] = [];
  calculatedAge: number = null;
  ageDisplay: string = '';
  maxAcquiredDate: Date = null;
  private previousErrors: Set<string> = new Set();
  showSumInsuredOver50kReferralMessage = false;
  showRestrictedSumInsuredOver20kReferralMessage = false;

  // Use of horse
  classOfUse: any[] = [];
  classes: any[] = [];
  selectedClass: any = null;
  allActivities: any[] = [];
  /** Activities for the selected class of use (from API equine/static-data/class-of-use/{class_id}); shown in "Activities for Class X" list only. */
  classActivitiesForDisplay: any[] = [];
  coverType: string = '';
  horseAge: number = null;
  isBloodstockCover: boolean = false;
  isRestrictedCover: boolean = false;
  isACECover: boolean = false;
  bloodstockLssSelected: boolean = false;
  isLoading: boolean = false;
  /** When ACE + Loss of Horse No: store sum insured so it can be restored when user switches back to Yes. */
  private aceLossOfHorsePreviousSumInsured: number | null = null;

  // Cover type
  coverTypes: any[] = [];

  // Cover details – options and flags
  /** DB UUIDs for vet fee / excess / co-insurance (must match backend seeders) */
  private static readonly VET_FEE_COVER = {
    3000: '1vfcac3f-b4a7-431e-9436-8d03cec4egth',
    4500: '2vfcac3f-b4a7-431e-9436-8d03cec4egth',
    6000: '3vfcac3f-b4a7-431e-9436-8d03cec4egth',
    7500: '4vfcac3f-b4a7-431e-9436-8d03cec4egth',
    5500: '5vfcac3f-b4a7-431e-9436-8d03cec4egth',
    '7500_ace': '8vfcac3f-b4a7-431e-9436-8d03cec4egth',
    '7500_bloodstock': '9vfcac3f-b4a7-431e-9436-8d03cec4egth'
  };
  private static readonly VET_FEE_EXCESS: Record<string, string> = {
    150: '1vfxac3f-b4a7-431e-9436-8d03cec4er56',
    250: '2vfxac3f-b4a7-431e-9436-8d03cec4er56',
    375: '3vfxac3f-b4a7-431e-9436-8d03cec4er56',
    500: '4vfxac3f-b4a7-431e-9436-8d03cec4er56',
    '175_20': '5vfxac3f-b4a7-431e-9436-8d03cec4er56',
    '375_20': '6vfxac3f-b4a7-431e-9436-8d03cec4er56',
    '500_essential': '7vfxac3f-b4a7-431e-9436-8d03cec4er56',
    200: '8vfxac3f-b4a7-431e-9436-8d03cec4er56',
    350: '9vfxac3f-b4a7-431e-9436-8d03cec4er56',
    '500_restricted': '10vfxa3f-b4a7-431e-9436-8d03cec4er56',
    '350_ace': '11vfxa3f-b4a7-431e-9436-8d03cec4er56',
    '350_bloodstock': '12vfxa3f-b4a7-431e-9436-8d03cec4er56'
  };
  private static readonly VET_FEE_CO_INSURANCE = {
    0: '1vfcic3f-b4a7-431e-9436-8d03cec4e3r5',
    15: '2vfcic3f-b4a7-431e-9436-8d03cec4e3r5',
    20: '3vfcic3f-b4a7-431e-9436-8d03cec4e3r5',
    25: '4vfcic3f-b4a7-431e-9436-8d03cec4e3r5',
    30: '5vfcic3f-b4a7-431e-9436-8d03cec4e3r5'
  };
  readonly VET_FEE_EXCESS_150_ID = EquineQuoteDetailsComponent.VET_FEE_EXCESS[150];
  readonly CO_INSURANCE_20_ID = EquineQuoteDetailsComponent.VET_FEE_CO_INSURANCE[20];
  readonly CO_INSURANCE_IDS_20_OR_MORE = [
    EquineQuoteDetailsComponent.VET_FEE_CO_INSURANCE[20],
    EquineQuoteDetailsComponent.VET_FEE_CO_INSURANCE[25],
    EquineQuoteDetailsComponent.VET_FEE_CO_INSURANCE[30]
  ];

  louActivities: { id: string; name: string }[] = [];
  vfEnhancedOptions: { id: string; label: string; minSi?: number; maxAge?: number }[] = [
    { id: '1vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£3000 per incident (£6000 aggregate)' },
    { id: '2vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£4500 per incident (£9000 aggregate)' },
    { id: '3vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£6000 per incident (£12,000 aggregate)', maxAge: 19 },
    { id: '4vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£7500 per incident (£15,000 aggregate)', minSi: 5001, maxAge: 19 }
  ];
  vfFixedExcessOptions: { id: string; label: string; minCoInsurance?: number }[] = [
    { id: '1vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£150', minCoInsurance: 20 },
    { id: '2vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£250' },
    { id: '3vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£375' },
    { id: '4vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£500' }
  ];
  vfCoInsuranceOptions: { id: string; label: string; value: number }[] = [
    { id: '1vfcic3f-b4a7-431e-9436-8d03cec4e3r5', label: '0%', value: 0 },
    { id: '2vfcic3f-b4a7-431e-9436-8d03cec4e3r5', label: '15%', value: 15 },
    { id: '3vfcic3f-b4a7-431e-9436-8d03cec4e3r5', label: '20%', value: 20 },
    { id: '4vfcic3f-b4a7-431e-9436-8d03cec4e3r5', label: '25%', value: 25 },
    { id: '5vfcic3f-b4a7-431e-9436-8d03cec4e3r5', label: '30%', value: 30 }
  ];
  vfEssentialExcessOptions: { id: string; label: string }[] = [
    { id: '7vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£500' },
    { id: '5vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£175 + 20%' },
    { id: '6vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£375 + 20%' }
  ];
  /** Excess options when Restricted cover is selected */
  vfRestrictedExcessOptions: { id: string; label: string }[] = [
    { id: '8vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£200' },
    { id: '9vfxac3f-b4a7-431e-9436-8d03cec4er56', label: '£350' },
    { id: '10vfxa3f-b4a7-431e-9436-8d03cec4er56', label: '£500' }
  ];
  /** Veterinary Fees cover amount options for Restricted cover */
  vfRestrictedCoverOptions: { id: string; label: string }[] = [
    { id: '6vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£3500 per annum' },
    { id: '7vfcac3f-b4a7-431e-9436-8d03cec4egth', label: '£5000 per annum' }
  ];
  readonly vfRestrictedCoverHelpText = 'All cover is restricted to Accidental External Injury Only i.e. there is no cover for illness, sickness or disease. Cover is per incident and in aggregate, which means the amount selected is the total benefit per policy period.';
  requiresLouReferral = false; // set true when "Other" LOU + free text entered

  /** Expose Number for template (Angular templates only see component properties) */
  Number = Number;

  // Dates – min inception: brokers cannot backdate; Peliwica can (e.g. up to 1 year back)
  minInceptionDate = new Date();
  maxInceptionDate = new Date();
  minEffectiveDate = new Date();
  maxEffectiveDate = new Date();
  maxDob: Date = new Date();
  policyEndDate: Date = null;

  // Cancellation logic
  cancellationSubType: 'ntu' | 'lapsed' | 'standard' = 'standard';

  get isCancellation(): boolean { return this.quote_type === 4; }
  get isMta(): boolean { return this.quote_type === 3; }
  get reasonIsOther(): boolean {
    const id = this.formFields?.get('mta_reason_id')?.value;
    const reason = this.policyReasons.find((r) => r.id === id);
    return (reason?.reasons || '').trim().toLowerCase() === 'other';
  }

  private updateMtaReasonOtherValidators(): void {
    const ctrl = this.formFields?.get('mta_reason_other');
    if (!ctrl) {
      return;
    }
    if (this.reasonIsOther) {
      ctrl.setValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private applyStoredTransactionType(): void {
    const routeType = this.route.snapshot.params.quote_type != null
      ? +this.route.snapshot.params.quote_type
      : null;
    if (routeType === 3 || routeType === 4) {
      return;
    }
    const stored = Number(this.modelObject?.type ?? this.modelObject?.transaction_type);
    if (stored !== 3 && stored !== 4) {
      return;
    }
    this.quote_type = stored;
    this.modelObject.previous_policy_id = resolveEquinePreviousPolicyId(this.modelObject);
    this.formFields.get('mta_effective_date').setValidators(Validators.required);
    this.formFields.get('mta_reason_id').setValidators(Validators.required);
    this.formFields.get('mta_effective_date').updateValueAndValidity({ emitEvent: false });
    this.formFields.get('mta_reason_id').updateValueAndValidity({ emitEvent: false });
    this.formFields.get('transaction_type').setValue(stored, { emitEvent: false });
    this.getPolicyReasons();
  }

  get cancellationLabel(): string {
    if (this.cancellationSubType === 'ntu') return 'NTU';
    if (this.cancellationSubType === 'lapsed') return 'Lapsed';
    return 'Cancellation';
  }

  private static readonly TRANSACTION_TYPE_LABELS: Record<number, string> = {
    1: 'New Business', 2: 'Renewal', 3: 'MTA', 4: 'Cancellation', 5: 'Lapsed', 6: 'NTU',
  };

  get transactionTypeLabel(): string {
    if (this.isCancellation) return this.cancellationLabel;
    return EquineQuoteDetailsComponent.TRANSACTION_TYPE_LABELS[this.quote_type] || 'New Business';
  }

  get cancellationSuccessMessage(): string {
    if (this.cancellationSubType === 'ntu') return 'Policy processed as NTU successfully';
    if (this.cancellationSubType === 'lapsed') return 'Policy processed as Lapsed successfully';
    return 'Policy cancelled successfully';
  }

  // Title list
  titleList = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Prof'];

  constructor(
    private formBuilder: FormBuilder,
    private equineService: EquineService,
    private quoteService: QuoteService,
    private route: ActivatedRoute,
    private router: Router,
    private sessionState: LocalStorageService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private userService: UserInfoService,
    private productBasedConfigurationService: ProductBasedConfigurationService,
    private el: ElementRef,
    private modalService: NgbModal,
    private policyService: PolicyService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.quote_type === 3 || this.quote_type === 4) {
      this.formFields.get('mta_effective_date').setValidators(Validators.required);
      this.formFields.get('mta_effective_date').updateValueAndValidity({ emitEvent: false });
      this.formFields.get('transaction_type').setValue(this.quote_type, { emitEvent: false });
    }
    if (this.quote_type === 3 || this.quote_type === 4) {
      this.formFields.get('mta_reason_id').setValidators(Validators.required);
      this.formFields.get('mta_reason_id').updateValueAndValidity({ emitEvent: false });
      this.getPolicyReasons();
    }
    if (this.sessionState.retrieve('is_broker')) {
      this.isBroker = true;
      this.formFields.get('broker_id').setValue(this.sessionState.retrieve('broker_company_id'));
      this.formFields.get('broker_id').disable();
    }
    this.setupDateRestrictions();
    this.generateYearOptions();
    this.loadOwnershipStatuses();
    this.getCoverTypes();
    this.loadStaticData();
    this.calculateHorseAge();
    this.updateAvailableCoverTypes();
    this.updateAvailableClasses();
    this.getBrokers();
    this.getPostcodes(this.sessionState.retrieve('broker_company_id'), environment.NEW_EQUINE_ID);

    this.formFields.get('proposer_details.forename').valueChanges.subscribe((value) => {
      const capitalizedValue = capitalizeName(value);
      this.formFields.get('proposer_details.forename').setValue(capitalizedValue, { emitEvent: false });
    })

    this.formFields.get('proposer_details.surname').valueChanges.subscribe((value) => {
      const capitalizedValue = capitalizeName(value);
      this.formFields.get('proposer_details.surname').setValue(capitalizedValue, { emitEvent: false });
    })

    this.formFields.get('horse_details.stable_name').valueChanges.subscribe((value) => {
      const formatted = capitalizeHorseName(value);
      this.formFields.get('horse_details.stable_name').setValue(formatted, { emitEvent: false });
    });

    this.formFields.get('horse_details.passport_name').valueChanges.subscribe((value) => {
      const formatted = capitalizeHorseName(value);
      this.formFields.get('horse_details.passport_name').setValue(formatted, { emitEvent: false });
    });

    this.formFields.get('policy_start_date').valueChanges.subscribe((value) => {
      this.maxAcquiredDate = new Date(value);
    });

    this.address.get('postcode').valueChanges.subscribe((value) => {
      const formatted = this.formatUKPostcode(value);
      if (formatted !== value) {
        this.address.get('postcode').setValue(formatted, { emitEvent: false });
      }
      if (this.suppressPostcodeLookup) {
        return;
      }
      this.postcodeLookup(formatted || value || '');
    });

    this.autoSaveSubscription = this.autoSaveSubject.pipe(debounceTime(400)).subscribe(() => {
      this.performAutoSave();
    });

    this.postcodeLookupSubscription = this.postcodeLookupSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe((postcode) => {
      this.doPostcodeLookup(postcode);
    });
  }

  ngOnDestroy() {
    if (this.autoSaveSubscription) {
      this.autoSaveSubscription.unsubscribe();
    }
    if (this.postcodeLookupSubscription) {
      this.postcodeLookupSubscription.unsubscribe();
    }
  }
  
  /**
   * Formats a UK postcode to be uppercase with a single space 
   * between the outward and inward segments.
   * * @param postcode - The raw postcode string (e.g., "sw1a1aa" or "m1 1aa")
   * @returns A formatted postcode string (e.g., "SW1A 1AA") or the original if invalid
   */
  public formatUKPostcode(postcode: string): string {
    if (!postcode) return '';

    const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, '');
    const len = cleaned.length;

    if (postcode.includes(' ')) {
      const parts = postcode.trim().toUpperCase().split(/\s+/);
      if (parts.length === 2 && /^\d[A-Z]{2}$/.test(parts[1])) {
        return `${parts[0]} ${parts[1]}`;
      }
    }

    // Only insert the space once the inward code looks complete (digit + 2 letters),
    // so partial values like NP443G are not reformatted as NP4 43G.
    const inward = cleaned.slice(-3);
    if (len >= 5 && len <= 7 && /^\d[A-Z]{2}$/.test(inward)) {
      return `${cleaned.slice(0, -3)} ${inward}`;
    }

    return cleaned;
  }
  
  /** True when quote already exists, or minimum header fields through surname are filled for first create. */
  private canAutoSaveQuote(): boolean {
    if (this.modelObject?.id || this.quoteID) {
      return true;
    }
    if (!this.formFields) {
      return false;
    }
    const v = this.formFields.getRawValue();
    const pd = v?.proposer_details || {};
    const hasValue = (x: any) => x != null && String(x).trim() !== '';
    const brokerId = v?.broker_id || this.modelObject?.broker_id
      || (this.isBroker ? this.sessionState.retrieve('broker_company_id') : null);
    return hasValue(v?.policy_start_date)
      && hasValue(v?.period_of_cover_to)
      && hasValue(brokerId)
      && hasValue(pd?.title)
      && hasValue(pd?.forename)
      && hasValue(pd?.surname);
  }

  requestAutoSave(): void {
    if (this.suppressAutoSave) return;
    if (!this.canAutoSaveQuote()) return;
    this.formChangedByUser = true;
    this.autoSaveSubject.next();
  }

  /** @deprecated use requestAutoSave */
  requestPremiumUpdate(): void {
    this.requestAutoSave();
  }

  /** Called from template when user selects fixed excess; ensures premium update runs. */
  public onVetFeeExcessSelect(optId: string): void {
    this.policyCover.get('vet_fee_excess_id').setValue(optId);
    this.requestAutoSave();
  }

  /** Called from template when user selects co-insurance; ensures premium update runs. */
  public onVetFeeCoInsuranceSelect(optId: string): void {
    this.policyCover.get('vet_fee_co_insurance_id').setValue(optId);
    this.requestAutoSave();
  }

  /** Called from template when user toggles Life-Saving Surgery add-on; ensures premium update runs. */
  public onLifeSavingSurgeryToggle(): void {
    if (this.isLifeSavingFree) return;
    const c = this.policyCover.get('is_life_saving_surgery_selected');
    const newVal = Number(c.value) === 1 ? 0 : 1;
    c.setValue(newVal);
    this.requestAutoSave();
  }

  /** Bloodstock-specific: toggles £7500/£350 vet fee without setting is_life_saving_surgery_selected. */
  public onBloodstockLssToggle(selected: boolean): void {
    this.bloodstockLssSelected = selected;
    if (selected) {
      this.policyCover.get('is_vet_fee_selected').setValue(1, { emitEvent: false });
      this.policyCover.get('is_life_saving_surgery_selected').setValue(1, { emitEvent: false });
      this.policyCover.get('vet_fee_id').setValue(EquineQuoteDetailsComponent.VET_FEE_COVER['7500_bloodstock'], { emitEvent: false });
      this.policyCover.get('vet_fee_excess_id').setValue(EquineQuoteDetailsComponent.VET_FEE_EXCESS['350_bloodstock'], { emitEvent: false });
    } else {
      this.policyCover.get('is_vet_fee_selected').setValue(0, { emitEvent: false });
      this.policyCover.get('vet_fee_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_excess_id').setValue(null, { emitEvent: false });
      this.policyCover.get('is_life_saving_surgery_selected').setValue(0, { emitEvent: false });
    }
    this.requestAutoSave();
  }

  /** Called from template when user selects Yes/No for Permanent Loss of Use; ensures premium update runs. */
  onPermanentLouSelect(value: number): void {
    const louControl = this.policyCover.get('is_cover_for_permanent_lou');
    if (Number(louControl.value) === value) return;
    louControl.setValue(value, { emitEvent: true });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.getPostcodes(this.sessionState.retrieve('broker_company_id'), environment.NEW_EQUINE_ID);

    const staticDataChanged = !!(changes.staticData && this.staticData);
    const modelChanged = !!(changes.modelObject?.currentValue);

    // 1. Load static data into lookup lists when it arrives
    if (staticDataChanged) {
      this.loadOwnershipStatuses(this.initialPatchDone);
      this.getCoverTypes();
      this.loadStaticData();
    }

    // 2. Accept new quote data when it arrives
    if (modelChanged) {
      const modelChange = changes.modelObject.currentValue;
      this.modelObject = modelChange;
      if (modelChange.id) {
        this.quoteID = modelChange.id;
      }
      this.applyStoredTransactionType();
      this.generateYearOptions();
    }

    // 3. Run field checks whenever either input changed, but only if both are available
    const hasModel = this.modelObject && Object.keys(this.modelObject).length > 0;
    if ((staticDataChanged || modelChanged) && this.staticData && hasModel) {
      this.getCoverTypes();
      this.calculateHorseAge();
      this.updateAvailableCoverTypes();
      this.updateAvailableClasses();
      this.checkCoverType();

      if (this.isBloodstockCover) {
        this.loadBloodstockDropdownOptions();
      }

      if (this.quote_type === 3 || this.quote_type === 4) {
        this.formFields.get('mta_effective_date').setValidators(Validators.required);
      } else {
        this.formFields.get('mta_effective_date').clearValidators();
      }
      if (this.quote_type === 3 || this.quote_type === 4) {
        this.formFields.get('mta_reason_id').setValidators(Validators.required);
      } else {
        this.formFields.get('mta_reason_id').clearValidators();
      }
      this.formFields.get('mta_effective_date').updateValueAndValidity({ emitEvent: false });
      this.formFields.get('mta_reason_id').updateValueAndValidity({ emitEvent: false });

      // 4. Patch form values once when both static data and model are ready (model must have real data with an id)
      if (this.skipPatchFromPremiumUpdate) {
        this.skipPatchFromPremiumUpdate = false;
      } else if (!this.initialPatchDone && this.modelObject.id) {
        this.patchFormValues();
        this.evaluateCancellationRules();
        this.initialPatchDone = true;
      } else if (this.initialPatchDone && modelChanged && this.formFields) {
        this.syncHorseOwnershipFromModel();
      }
    }
  }

  get horseDetails(): FormGroup { return this.formFields.get('horse_details') as FormGroup; }
  get policyCover(): FormGroup { return this.formFields.get('policy_cover_details') as FormGroup; }
  get address(): FormGroup{ return this.formFields.get('proposer_details.address') as FormGroup; }

  /** Min date for MTA/Cancellation effective date (policy start). */
  get effectiveDateMin(): Date | null {
    const v = this.formFields?.get('policy_start_date')?.value;
    return v instanceof Date ? v : (v ? new Date(v) : null);
  }
  /** Max date for MTA/Cancellation effective date (policy end or maxInceptionDate). */
  get effectiveDateMax(): Date | null {
    const v = this.formFields?.get('period_of_cover_to')?.value;
    if (v instanceof Date) return v;
    if (v) return new Date(v);
    return this.maxInceptionDate || null;
  }

  initializeForm() {
    this.formFields = this.formBuilder.group({
      policy_start_date: ['', Validators.required],
      period_of_cover_to: ['', Validators.required],
      mta_effective_date: [''],
      mta_reason_id: [''],
      mta_reason_other: [''],
      broker_id: ['', Validators.required],
      transaction_type: [1, Validators.required],
      proposer_details: this.formBuilder.group({
        title: ['', Validators.required],
        forename: ['', [Validators.required, this.capitalizeValidator]],
        surname: ['', [Validators.required, this.capitalizeValidator]],
        date_of_birth: ['', Validators.required],
        address: this.formBuilder.group({
          postcode: ['', [Validators.required, (control) => this.postcodeValidator(control)]],
          address1: ['', Validators.required],
          address2: [''],
          town_county: ['', Validators.required]
        }),
        phone_number: ['', Validators.required],
        email_address: ['', [Validators.required, Validators.email]]
      }),
      horse_details: this.formBuilder.group({
        stable_name: ['', [Validators.required, this.capitalizeValidator]],
        passport_name: ['', [Validators.required, this.capitalizeValidator]],
        acquired_date: ['', Validators.required],
        ownership_status: ['', Validators.required],
        purchase_price: ['', [Validators.required, this.wholeNumberValidator]],
        sum_insured: ['', [Validators.required, this.wholeNumberValidator]],
        horse_birth_year: ['', Validators.required],
        age: [{ value: '', disabled: true }]
      }),
      policy_cover_details: this.formBuilder.group({
        cover_type_id: ['', Validators.required],
        cover_sub_types_id: ['1send37f-b4a7-431e-9436-8d03cecceac0', Validators.required],
        class_of_use_id: ['', Validators.required],
        main_use_of_horse_id: [''],
        is_elective_surgery: [0],
        is_disposal_euthansia: [0],
        is_cover_for_permanent_lou: [0],
        lou_activity_id: [''],
        lou_other_specify: [''],
        is_vet_fee_selected: [null],
        vet_fee_cover_type: [''], // 1 = Enhanced, 2 = Essential
        vet_fee_id: [''], // enhanced option i
        vet_fee_excess_id: [''],
        vet_fee_co_insurance_id: [''],
        vet_fee_essential_excess_id: [''],
        is_livery_hispitalization: [0],
        is_life_saving_surgery_selected: [],
        is_loss_of_horse_selected: [null],
        bloodstock_id: [null],
    }),
    });

    this.setupHorseDetailsSubscriptions();
    this.setupPolicyCoverSubscriptions();
    this.setupAutoSaveSubscriptions();
  }

  private setupAutoSaveSubscriptions(): void {
    // Selects / non-text fields still save on change (gated until surname for first create).
    // Text inputs save on blur via template (blur)="requestAutoSave()".
    const paths = [
      'mta_reason_id',
      'broker_id',
      'proposer_details.title',
      'proposer_details.date_of_birth',
      'horse_details.acquired_date',
      'horse_details.ownership_status',
    ];
    paths.forEach(path => {
      const control = this.formFields.get(path);
      if (control) {
        control.valueChanges.subscribe(() => {
          if (path === 'mta_reason_id') {
            if (!this.reasonIsOther) {
              this.formFields.get('mta_reason_other').setValue('', { emitEvent: false });
            }
            this.updateMtaReasonOtherValidators();
          }
          this.requestAutoSave();
        });
      }
    });

    // Same as cancellation reason: changing effective date triggers save; also refresh MTA/cancel premium preview.
    this.formFields.get('mta_effective_date')?.valueChanges
      .pipe(distinctUntilChanged((a, b) => {
        const aKey = a ? new Date(a).getTime() : null;
        const bKey = b ? new Date(b).getTime() : null;
        return aKey === bKey;
      }))
      .subscribe((value) => {
        if (!value) {
          return;
        }
        this.refreshTransactionPremiumPreview();
        this.requestAutoSave();
      });
  }

  public setupHorseDetailsSubscriptions() {
    this.horseDetails.get('horse_birth_year').valueChanges.subscribe(() => {
      this.calculateAge();
      this.calculateHorseAge();
      this.updateAvailableCoverTypes();
      this.updateAvailableClasses();
      this.applyCoverDetailsRules();
      this.clearUnavailableVetFeeSelection();
      this.requestAutoSave();
    });
    this.horseDetails.get('ownership_status').valueChanges.subscribe(() => this.onOwnershipStatusChange());
    this.horseDetails.get('acquired_date').valueChanges.subscribe(() => {
      this.onAcquiredDateChange();
      this.validateSumInsured();
    });
    this.horseDetails.get('purchase_price').valueChanges.subscribe(() => this.validateSumInsured());
    this.horseDetails.get('sum_insured').valueChanges.subscribe(() => {
      this.validateSumInsured();
      this.applyCoverDetailsRules();
      this.clearUnavailableVetFeeSelection();
    });
  }

  public setupPolicyCoverSubscriptions() {
    const requestPremium = () => this.requestAutoSave();

    this.policyCover.get('cover_type_id').valueChanges.subscribe(value => {
      this.onCoverTypeChange(value);
      requestPremium();
    });
    this.policyCover.get('class_of_use_id').valueChanges.subscribe(value => {
      const mainUseControl = this.policyCover.get('main_use_of_horse_id');
      if (value) {
        mainUseControl.setValidators([Validators.required]);
        this.selectedClass = this.classes.find(c => c.id === value || c.id == value) || null;
        if (value !== EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID) {
          this.loadClassActivitiesForDisplay(value);
        } else {
          this.classActivitiesForDisplay = [];
        }
      } else {
        mainUseControl.clearValidators();
        mainUseControl.setValue('');
        this.selectedClass = null;
        this.classActivitiesForDisplay = [];
      }
      mainUseControl.updateValueAndValidity();
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('main_use_of_horse_id').valueChanges.subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('bloodstock_id').valueChanges.subscribe(() => requestPremium());
    this.policyCover.get('is_cover_for_permanent_lou').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('is_vet_fee_selected').valueChanges.subscribe((val) => {
      if (Number(val) !== 1) {
        const noEmit = { emitEvent: false };
        this.policyCover.get('vet_fee_id').setValue(null, noEmit);
        this.policyCover.get('vet_fee_excess_id').setValue(null, noEmit);
        this.policyCover.get('vet_fee_co_insurance_id').setValue(null, noEmit);
        this.policyCover.get('vet_fee_essential_excess_id').setValue(null, noEmit);
        this.policyCover.get('vet_fee_cover_type').setValue(null, noEmit);
        this.policyCover.get('is_livery_hispitalization').setValue(0, noEmit);
        this.policyCover.get('is_life_saving_surgery_selected').setValue(0, noEmit);
        if (this.isBloodstockCover) {
          this.bloodstockLssSelected = false;
        }
      }
      this.applyCoverDetailsRules();
      if (Number(val) !== 1) {
        this.requestAutoSave();
      }
    });
    this.policyCover.get('vet_fee_id').valueChanges.subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('vet_fee_excess_id').valueChanges.subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('vet_fee_co_insurance_id').valueChanges.subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('vet_fee_essential_excess_id').valueChanges.subscribe(() => {
      this.applyCoverDetailsRules();
      requestPremium();
    });
    this.policyCover.get('is_elective_surgery').valueChanges.subscribe(requestPremium);
    this.policyCover.get('is_disposal_euthansia').valueChanges.subscribe(requestPremium);
    this.policyCover.get('is_livery_hispitalization').valueChanges.subscribe(requestPremium);
    this.policyCover.get('is_life_saving_surgery_selected').valueChanges.subscribe(() => requestPremium());
    this.policyCover.get('is_loss_of_horse_selected').valueChanges.subscribe((value: number) => {
      this.onAceLossOfHorseChange(value);
      requestPremium();
    });
    this.policyCover.get('lou_activity_id').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.updateLouOtherSpecifyValidators();
      this.applyLouOtherReferral();
      requestPremium();
    });
    this.policyCover.get('lou_other_specify').valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
      this.applyLouOtherReferral();
    });
}

  /**
   * Setup restrictions on date pickers
   */
  public setupDateRestrictions() {
    // Period of cover from: brokers cannot backdate (min = today); Peliwica can backdate (min = 1 year ago)
    if (this.isBroker) {
      this.minInceptionDate = new Date();
    } else {
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 1);
      this.minInceptionDate = minDate;
    }

    // 30 days ahead
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    // set 30 max
    this.maxInceptionDate = maxDate;

    // DOB: must be 18 years before inception date
    const minDobDate = new Date();
    minDobDate.setFullYear(minDobDate.getFullYear() - 18);
    this.maxDob = minDobDate;
  }

  get f(): { [key: string]: AbstractControl } { return this.formFields.controls; }
  get proposerControls() { return (this.formFields.get('proposer_details') as FormGroup).controls; }
  get addressControls() { return ((this.proposerControls.address as FormGroup).controls); }

  public onPolicyStartDateChange(event) {
    if (event.value) {
      const startDate = new Date(event.value);
      this.maxAcquiredDate = startDate;
      if (this.quote_type !== 3 && this.quote_type !== 4) {
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(endDate.getDate() - 1);
        this.policyEndDate = endDate;
        this.formFields.get('period_of_cover_to').setValue(endDate);
      }
      this.requestAutoSave();
    }
  }

  openEffectiveDatePicker(): void {
    if (this.cancellationSubType === 'ntu') {
      return;
    }
    this.effectiveDateDatepicker?.open();
  }

  onEffectiveDateChange(event) {
    if (!event?.value) {
      return;
    }
    const inceptionDate = this.formFields.get('policy_start_date').value;
    const coverToDate = this.formFields.get('period_of_cover_to').value;
    const effectiveDate = new Date(event.value);

    if (inceptionDate && coverToDate) {
      const min = new Date(inceptionDate);
      const max = new Date(coverToDate);
      min.setHours(0, 0, 0, 0);
      max.setHours(0, 0, 0, 0);
      effectiveDate.setHours(0, 0, 0, 0);
      if (effectiveDate < min || effectiveDate > max) {
        this._snackBar.open('Effective date must be within policy term dates', 'Close', {
          duration: 3000
        });
        this.formFields.get('mta_effective_date').setValue(null, { emitEvent: false });
      }
    }
  }

  private refreshTransactionPremiumPreview(): void {
    if (this.quote_type !== 3 && this.quote_type !== 4) {
      return;
    }
    const policyId = this.modelObject?.previous_policy_id || this.modelObject?.id || this.quoteID;
    if (!policyId) {
      return;
    }
    const effectiveVal = this.formFields?.get('mta_effective_date')?.value;
    const effectiveDate = effectiveVal
      ? formatDate(effectiveVal, 'yyyy-MM-dd', 'en-GB')
      : undefined;
    const prevPolicyId = this.modelObject?.previous_policy_id || policyId;
    const txType = this.quote_type;
    this.equineService.getEquineData(policyId, { previewType: txType, effectiveDate }).subscribe({
      next: (response: any) => {
        if (!response?.success || !response.result?.premiums?.data) {
          return;
        }
        this.modelObject = {
          ...this.modelObject,
          premiums: response.result.premiums,
          type: txType,
          transaction_type: txType,
          previous_policy_id: prevPolicyId,
        };
        this.modelObjectUpdated.emit(this.modelObject);
      },
    });
  }

  /**
   * Determine cancellation sub-type: NTU (New Business within 14 days) or Lapsed (Renewal within 14 days).
   * Claims handling and effective date logic are managed by the backend.
   */
  private evaluateCancellationRules(): void {
    if (!this.isCancellation) return;

    const policyStart = this.modelObject?.policy_start_date;
    if (!policyStart) return;

    const inception = new Date(policyStart);
    inception.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today.getTime() - inception.getTime()) / (1000 * 60 * 60 * 24));
    const within14Days = diffDays <= 14;

    const origType = Number(this.modelObject?.original_transaction_type) || 1;

    if (within14Days && origType === 1) {
      this.cancellationSubType = 'ntu';
      this.applyCancellationInceptionDate(policyStart);
    } else if (within14Days && origType === 2) {
      this.cancellationSubType = 'lapsed';
    } else {
      this.cancellationSubType = 'standard';
    }
  }

  private applyCancellationInceptionDate(policyStart: string | Date): void {
    const inceptionDate = new Date(policyStart);
    inceptionDate.setHours(0, 0, 0, 0);
    this.formFields.get('mta_effective_date')?.setValue(inceptionDate, { emitEvent: false });
    this.refreshTransactionPremiumPreview();
    this.requestAutoSave();
  }

  /** Called on input/value change; pushes to debounced subject so we don’t ping the API while typing. */
  postcodeLookup(eventOrValue: any) {
    if (this.suppressPostcodeLookup) {
      return;
    }
    const postcode = (
      typeof eventOrValue === 'string'
        ? eventOrValue
        : (eventOrValue?.target?.value || '')
    ).trim();
    if (!postcode || postcode.length < 4) {
      this.addresses = [];
      return;
    }
    this.postcodeLookupSubject.next(postcode);
  }

  /** Backend-proxied postcode lookup (address/search). Called after debounce. */
  private doPostcodeLookup(postcode: string) {
    if (this.suppressPostcodeLookup) {
      return;
    }
    const cleaned = (postcode || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!cleaned || cleaned.length < 4) {
      this.addresses = [];
      this.cdr.detectChanges();
      return;
    }
    const token = ++this.postcodeLookupToken;
    this.quoteService.getAddress(cleaned).subscribe({
      next: (response: any) => {
        if (token !== this.postcodeLookupToken || this.suppressPostcodeLookup) {
          return;
        }
        const body = response?.body ?? response;
        const records = body?.records ?? body?.addresses ?? response?.records ?? response?.addresses ?? [];
        this.addresses = Array.isArray(records) ? records : [];
        this.cdr.detectChanges();
        if (this.addresses.length > 0) {
          this.postcodeAutoTrigger?.openPanel();
        }
      },
      error: () => {
        if (token !== this.postcodeLookupToken) {
          return;
        }
        this.addresses = [];
        this.cdr.detectChanges();
      }
    });
  }

  /** Called when user selects an address from the list. Supports backend lookup (id + l) and legacy (full object). */
  onSelectAddress(record: any) {
    if (!record) return;
    this.suppressPostcodeLookup = true;
    this.postcodeLookupToken++;
    this.addresses = [];
    this.postcodeAutoTrigger?.closePanel();
    const currentPostcode = this.addressControls.postcode.value;
    // Backend/Simply: record has id and l; fetch full address by id
    if (record.id != null && record.id !== '') {
      this.quoteService.getAddressById(record.id).subscribe({
        next: (full: any) => {
          this.applyAddressToForm(full);
          setTimeout(() => {
            if (this.addressControls.postcode.value !== currentPostcode) {
              this.addressControls.postcode.setValue(currentPostcode, { emitEvent: false });
            }
            this.suppressPostcodeLookup = false;
          }, 0);
        },
        error: () => {
          this.suppressPostcodeLookup = false;
        }
      });
      return;
    }
    // Legacy format (e.g. getAddress.io): full address object
    this.applyAddressToForm(record);
    setTimeout(() => {
      if (this.addressControls.postcode.value !== currentPostcode) {
        this.addressControls.postcode.setValue(currentPostcode, { emitEvent: false });
      }
      this.suppressPostcodeLookup = false;
    }, 0);
  }

  /** Map API address object to form (Simply: line1/Line1/line_1, town, county, postcode; legacy: line_1, town_or_city, etc.). */
  private applyAddressToForm(address: any, selectedPostcode: string = '') {
    if (!address || !this.addressControls) return;
    const a = address;
    const line1 = a.line1;
    const line2 = a.line2;
    const town = a.town;
    const county = a.county;
    const postcode = a.postcode;
    const country = a.country;
    this.addressControls.address1.setValue(line1 || '', { emitEvent: false });
    this.addressControls.address2.setValue(line2 || '', { emitEvent: false });
    this.addressControls.town_county.setValue([town, county].filter(Boolean).join(', ') || '', { emitEvent: false });
    this.addressControls.postcode.setValue(this.formatUKPostcode(postcode || '') || postcode || '', { emitEvent: false });
    this.addressControls.postcode.updateValueAndValidity({ emitEvent: false });
    if (this.addressControls.country && country) {
      this.addressControls.country.setValue(country, { emitEvent: false });
    }
    this.requestAutoSave();
  }

  capitalizeValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    const words = control.value.split(/[\s-]+/);
    for (const word of words) {
      if (word && word[0] !== word[0].toUpperCase()) {
        return { 'notCapitalized': true };
      }
    }
    return null;
  }

  postcodeValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    const postcode = control.value.toUpperCase().replace(/\s+/g, '');
    const excludedPrefixes = (this.excludedPrefixes?.length ? this.excludedPrefixes : ['JE', 'GY'])
      .map(prefix => String(prefix || '').trim().toUpperCase())
      .filter(prefix => !!prefix && prefix !== 'IM');
    for (const prefix of excludedPrefixes) {
      if (postcode.startsWith(prefix)) {
        return { 'excludedPostcode': true };
      }
    }
    return null;
  }

  wholeNumberValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value && control.value !== 0) {
      return null;
    }
    const value = Number(control.value);
    if (isNaN(value) || value < 0 || !Number.isInteger(value)) {
      return { 'notWholeNumber': true };
    }
    return null;
  }

  generateYearOptions() {
    const currentYear = new Date().getFullYear();
    this.yearsOfBirth = [];
    for (let i = 0; i < 40; i++) {
      this.yearsOfBirth.push(currentYear - i);
    }
  }

  public loadOwnershipStatuses(initialPatchDone = false) {
    if (this.staticData && this.staticData.ownership_statuses) {
      this.ownershipStatuses = this.staticData.ownership_statuses;
      this.onOwnershipStatusChange(initialPatchDone);
    }
  }

  public calculateAge() {
    const yearOfBirth = this.horseDetails.get('horse_birth_year').value;
    if (!yearOfBirth) {
      this.calculatedAge = null;
      this.ageDisplay = '';
      this.horseDetails.get('age').setValue('');
      return;
    }

    const currentYear = new Date().getFullYear();
    this.calculatedAge = currentYear - yearOfBirth;
    if (this.calculatedAge === 0) {
      this.ageDisplay = '<1';
      this.horseDetails.get('age').setValue('<1');
    } else {
      this.ageDisplay = this.calculatedAge.toString();
      this.horseDetails.get('age').setValue(this.calculatedAge);
    }
  }

  onAcquiredDateChange() {
    const acquiredDate = this.horseDetails.get('acquired_date').value;
    if (!acquiredDate) {
      const errors = this.horseDetails.get('acquired_date').errors;
      if (errors) {
        delete errors['acquiredAfterCover'];
        this.horseDetails.get('acquired_date').setErrors(Object.keys(errors).length ? errors : null);
      }
      return;
    }
    let coverStartDate = this.modelObject.policy_start_date || this.modelObject.period_of_cover_from;
    if (coverStartDate) {
      const coverDate = new Date(coverStartDate);
      const acquired = new Date(acquiredDate);
      coverDate.setHours(0, 0, 0, 0);
      acquired.setHours(0, 0, 0, 0);
      if (acquired > coverDate) {
        const currentErrors = this.horseDetails.get('acquired_date').errors || {};
        this.horseDetails.get('acquired_date').setErrors({ ...currentErrors, acquiredAfterCover: true });
      } else {
        const errors = this.horseDetails.get('acquired_date').errors;
        if (errors) {
          delete errors['acquiredAfterCover'];
          this.horseDetails.get('acquired_date').setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    }
    this.validateSumInsured();
  }

  /**
   * When the ownership status chnages and the owenership is not joint or purchased, set value to 0
   */
  public onOwnershipStatusChange(preserveValue = false) {
    const ownershipStatus = this.horseDetails.get('ownership_status').value;
    const purchasePriceControl = this.horseDetails.get('purchase_price');

    if (ownershipStatus && this.ownershipStatuses.length > 0) {
      const ownershipObj = this.ownershipStatuses.find(s => s.id == ownershipStatus);
      if (ownershipObj && !['Joint Owned', 'Purchased'].includes(ownershipObj.status_name)) {
        if (!preserveValue) purchasePriceControl.setValue(0);
        purchasePriceControl.disable();
      } else {
        if (!preserveValue) purchasePriceControl.setValue('');
        purchasePriceControl.enable();
      }
    } else if (!ownershipStatus) {
      purchasePriceControl.enable();
    }
    this.validateSumInsured();
  }

  public validateSumInsured() {
    const sumInsuredRaw = this.horseDetails.get('sum_insured').value;
    const purchasePriceRaw = this.horseDetails.get('purchase_price').value;
    const ownershipStatus = this.horseDetails.get('ownership_status').value;

    if (sumInsuredRaw === '') {
      this.previousErrors.clear();
      this.showSumInsuredOver50kReferralMessage = false;
      this.showRestrictedSumInsuredOver20kReferralMessage = false;
      return;
    } 

    const sumInsured = Number(sumInsuredRaw);
    const purchasePrice = Number(purchasePriceRaw);

    this.showSumInsuredOver50kReferralMessage = false;
    this.showRestrictedSumInsuredOver20kReferralMessage = false;
    if (Number.isFinite(sumInsured)) {
      if (this.isRestrictedCover && sumInsured > 20000) {
        this.showRestrictedSumInsuredOver20kReferralMessage = true;
      } else if (!this.isRestrictedCover && sumInsured > 50000) {
        this.showSumInsuredOver50kReferralMessage = true;
      }
    }

    const errors: any = {};

    if (ownershipStatus && !isNaN(purchasePrice) && purchasePriceRaw !== '' && purchasePriceRaw !== null && purchasePriceRaw !== undefined && purchasePrice > 0) {
      const ownershipObj = this.ownershipStatuses.find(s => s.id == ownershipStatus);

      if (ownershipObj && ownershipObj.status_name === 'Purchased' && this.isAcquiredWithin60Days()) {
        if (sumInsured > purchasePrice) {
          errors['sumInsuredExceedsPurchase'] = true;
        }

        const minSumInsured = purchasePrice * 0.5;
        if (sumInsured < minSumInsured) {
          errors['sumInsuredTooLow'] = true;
        }
      }
    }
    const currentErrors = this.horseDetails.get('sum_insured').errors || {};

    const finalErrors = { ...currentErrors, ...errors };
    delete finalErrors['sumInsuredOverLimit'];
    if (!errors['sumInsuredExceedsPurchase']) delete finalErrors['sumInsuredExceedsPurchase'];
    if (!errors['sumInsuredTooLow']) delete finalErrors['sumInsuredTooLow'];
    this.horseDetails.get('sum_insured').setErrors(Object.keys(finalErrors).length ? finalErrors : null);
  }

  private isAcquiredWithin60Days(): boolean {
    const acquiredRaw = this.horseDetails.get('acquired_date').value;
    if (!acquiredRaw) return false;
    const acquired = new Date(acquiredRaw);
    if (isNaN(acquired.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    acquired.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - acquired.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 60;
  }

  public getCoverTypes() {
    if (this.staticData && this.staticData.cover_types) {
      this.coverTypes = this.staticData.cover_types.map(type => ({ ...type, available: true }));


      const coverTypeId = this.getPc('cover_type_id');
      if (!this.initialPatchDone && coverTypeId && this.coverTypes.length) {
        const matching = this.coverTypes.find(t => t.id === coverTypeId);
        if (matching) {
          this.policyCover.get('cover_type_id').setValue(coverTypeId, { emitEvent: false });
        }
      }
    }
  }

  calculateHorseAge() {
    // Prefer form-derived age (from horse_birth_year) so UI reacts immediately when user changes age
    if (this.calculatedAge != null) {
      this.horseAge = this.calculatedAge;
      return;
    }

    if (this.horseDetails && this.horseDetails.get('age').value !== '' && this.horseDetails.get('age').value != null) {
      const ageVal = this.horseDetails.get('age').value;
      this.horseAge = ageVal === '<1' ? 0 : Number(ageVal);
      return;
    }
    
    const horseDetails = this.modelObject?.horse_Details || this.modelObject?.horse_details;
    if (horseDetails && horseDetails.age != 0) {
      this.horseAge = typeof horseDetails.age === 'string' && horseDetails.age === '<1' ? 0 : Number(horseDetails.age);
    } else {
      this.horseAge = null;
    }
  }

  /**
   * Update the available cover types based on the horse age
   * @returns 
   */
  public updateAvailableCoverTypes() {
    if (!this.policyCover || !this.coverTypes.length) return;
    if (this.horseAge === null) {
      this.coverTypes.forEach(t => t.available = true);
      return;
    }
    const restrictedCover = this.coverTypes.find(t => t.cover_type_name?.toLowerCase().includes('restricted'));
    const aceCover = this.coverTypes.find(t => t.cover_type_name?.toLowerCase().includes('ace'));
    const bloodstockCover = this.coverTypes.find(t => t.cover_type_name?.toLowerCase().includes('bloodstock'));
    const noEmit = { emitEvent: false };
    // check horse age
    if (this.horseAge >= 25) {
      if (restrictedCover) {
        this.coverTypes.forEach(t => {
          t.available = t.id === restrictedCover.id;
        });
      }
    } else {
      this.coverTypes.forEach(t => {
        if (restrictedCover && t.id === restrictedCover.id) t.available = true;
        else if (aceCover && t.id === aceCover.id) t.available = this.horseAge < 20;
        else if (bloodstockCover && t.id === bloodstockCover.id) t.available = this.horseAge < 15;
        else t.available = true;
      });
    }
    const currentValue = this.policyCover.get('cover_type_id').value;
    if (currentValue) {
      const selectedType = this.coverTypes.find(t => t.id === currentValue);
      if (selectedType && !selectedType.available) {
        this.policyCover.get('cover_type_id').setValue('', noEmit);
        this.policyCover.get('class_of_use_id').setValue('', noEmit);
        this.policyCover.get('main_use_of_horse_id').setValue('', noEmit);
      }
    }
  }

  public onCoverTypeChange(coverTypeId: string) {
    if (!coverTypeId) return;
    this.coverType = coverTypeId;

    const selectedCoverType = this.coverTypes.find(t => t.id === coverTypeId);
    const name = (selectedCoverType?.cover_type_name || '').toLowerCase();
    // check if cover type is bloodstock, restricted or ace
    this.isBloodstockCover = name.includes('bloodstock');
    this.isRestrictedCover = name.includes('restricted');
    this.isACECover = name.includes('ace');

    // this.policyCover.get('class_of_use_id').setValue(null, { emitEvent: false });
    // this.policyCover.get('main_use_of_horse_id').setValue(null, { emitEvent: false });
    this.policyCover.get('bloodstock_id').setValue(null, { emitEvent: false });
    this.policyCover.get('is_vet_fee_selected').setValue(null, { emitEvent: false });
    this.policyCover.get('vet_fee_cover_type').setValue(null, { emitEvent: false });
    this.policyCover.get('vet_fee_id').setValue(null, { emitEvent: false });
    this.policyCover.get('vet_fee_excess_id').setValue(null, { emitEvent: false });
    this.policyCover.get('vet_fee_co_insurance_id').setValue(null, { emitEvent: false });
    this.policyCover.get('vet_fee_essential_excess_id').setValue(null, { emitEvent: false });
    this.policyCover.get('is_livery_hispitalization').setValue(null, { emitEvent: false });
    this.policyCover.get('is_life_saving_surgery_selected').setValue(null, { emitEvent: false });
    this.policyCover.get('is_elective_surgery').setValue(null, { emitEvent: false });
    this.policyCover.get('is_disposal_euthansia').setValue(null, { emitEvent: false });
    this.policyCover.get('is_cover_for_permanent_lou').setValue(null, { emitEvent: false });
    this.policyCover.get('is_loss_of_horse_selected').setValue(null, { emitEvent: false });
    this.policyCover.get('lou_activity_id').setValue(null, { emitEvent: false });
    this.policyCover.get('lou_other_specify').setValue(null, { emitEvent: false });
    this.policyCover.get('is_vet_fee_selected').setValue(null, { emitEvent: false });
    

    // ACE cover: fixed Veterinary Fees £7500 per annum, £350 excess – set form so premium calculates correctly
    if (this.isACECover) {
      // this.policyCover.get('is_vet_fee_selected').setValue(1, { emitEvent: false });
      this.policyCover.get('vet_fee_cover_type').setValue(1, { emitEvent: false });
      this.policyCover.get('vet_fee_id').setValue(EquineQuoteDetailsComponent.VET_FEE_COVER['7500_ace'], { emitEvent: false });
      this.policyCover.get('vet_fee_excess_id').setValue(EquineQuoteDetailsComponent.VET_FEE_EXCESS['350_ace'], { emitEvent: false });
      this.policyCover.get('is_vet_fee_selected').setValue(1, { emitEvent: false });
    }

    // Restricted cover: set default vet fee options when vet fee is selected so premium can calculate
    if (this.isRestrictedCover && Number(this.policyCover.get('is_vet_fee_selected').value) === 1) {
      if (Number(this.policyCover.get('vet_fee_cover_type').value) === 1) {
        if (!this.policyCover.get('vet_fee_id').value) {
          this.policyCover.get('vet_fee_id').setValue(EquineQuoteDetailsComponent.VET_FEE_COVER[7500], { emitEvent: false });
        }
        if (!this.policyCover.get('vet_fee_excess_id').value) {
          this.policyCover.get('vet_fee_excess_id').setValue(EquineQuoteDetailsComponent.VET_FEE_EXCESS[350], { emitEvent: false });
        }
      }
    }

    // When switching away from ACE, restore sum insured required (Loss of Horse becomes Yes)
    const lossOfHorseControl = this.policyCover.get('is_loss_of_horse_selected');
    if (!this.isACECover) {
      const sumCtrl = this.horseDetails?.get('sum_insured');
      if (sumCtrl) {
        sumCtrl.setValidators([Validators.required, this.wholeNumberValidator]);
        sumCtrl.updateValueAndValidity({ emitEvent: false });
      }
      lossOfHorseControl.clearValidators();
    } else {
      lossOfHorseControl.setValidators([Validators.required]);
    }
    lossOfHorseControl.updateValueAndValidity({ emitEvent: false });

    // Get class of use
    const classControl = this.policyCover.get('class_of_use_id');
    const bloodstockControl = this.policyCover.get('bloodstock_id');
    if (this.isBloodstockCover) {
      classControl.clearValidators();
      classControl.setValue(EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID, { emitEvent: false });
      bloodstockControl.setValidators([Validators.required]);
      this.loadBloodstockDropdownOptions();
    } else {
      classControl.setValidators([Validators.required]);
      this.bloodstockDropdownOptions = [];
      bloodstockControl.clearValidators();
      bloodstockControl.setValue(null, { emitEvent: false });
    }
    classControl.updateValueAndValidity({ emitEvent: false });
    bloodstockControl.updateValueAndValidity({ emitEvent: false });
    this.updateAvailableClasses();

    if (this.isRestrictedCover && this.horseAge >= 25) {
      this.validateSumInsured();
    }

    if (selectedCoverType) {
      this.applyCoverDetailsRules();
    }
    this.validateSumInsured();
  }

  public validateSumInsuredCover() {
    this.validateSumInsured();
  }

  
  /**
   * @param coverTypeId 
   * @returns 
   */
  public selectCoverType(coverTypeId: string) {
    if (!this.isCoverTypeAvailable(coverTypeId)) return;
    this.policyCover.get('cover_type_id').setValue(coverTypeId);
    this.policyCover.get('is_livery_hispitalization').setValue(null, { emitEvent: false });
    this.policyCover.get('class_of_use_id').setValue(null, { emitEvent: false });
  }

  /**
   * @param coverTypeId 
   * @returns 
   */
  public isCoverTypeAvailable(coverTypeId: string): boolean {
    const coverType = this.coverTypes.find(t => t.id === coverTypeId);
    return coverType ? coverType.available : false;
  }

  /**
   * @param coverTypeId 
   * @returns boolean
   */
  public isCoverTypeSelected(coverTypeId: string): boolean {
    return this.policyCover.get('cover_type_id').value === coverTypeId;
  }

  loadStaticData() {
    if (this.staticData && this.staticData.class_of_use) {
      this.classOfUse = this.staticData.class_of_use;
      this.classes = this.classOfUse.filter(c => c.class_name !== 'Bloodstock' && c.class_name !== 'Main Use of Horse');
      
      this.allActivities = this.staticData.horse_activities;
      this.classes.sort((a, b) => a.class - b.class);
      const classOfUseId = this.getPc('horse_class_id') != null ? this.getPc('horse_class_id') : this.getPc('class_of_use_id');
      if (classOfUseId) {
        this.policyCover.get('class_of_use_id').setValue(classOfUseId, { emitEvent: false });
        this.selectedClass = this.classes.find(c => c.id === classOfUseId || c.id == classOfUseId) || null;
        if (classOfUseId !== EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID) {
          this.loadClassActivitiesForDisplay(classOfUseId);
        }
      }
    }
    if (this.staticData && Array.isArray(this.staticData.lou_activities)) {
      this.louActivities = this.staticData.lou_activities.map((a: any) => ({
        id: a.id,
        name: a.activity_name
      }));
    }
  }

  /** Load activities for the selected class from equine/static-data/class-of-use/{class_id} and set classActivitiesForDisplay. */
  private loadClassActivitiesForDisplay(classId: string) {
    this.equineService.getClassActivities(classId).subscribe({
      next: (res: any) => {
        const list = res?.result ?? res?.data ?? [];
        this.classActivitiesForDisplay = Array.isArray(list) ? list : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.classActivitiesForDisplay = [];
        this.cdr.detectChanges();
      }
    });
  }

  public checkCoverType() {
    const coverTypeId = this.getPc('cover_type_id') || this.policyCover.get('cover_type_id').value;
    if (coverTypeId) {
      this.coverType = coverTypeId;
      const selectedCoverType = this.coverTypes.find(t => t.id === this.coverType);
      const name = (selectedCoverType?.cover_type_name || '').toLowerCase();
      this.isBloodstockCover = name.includes('bloodstock');
      this.isRestrictedCover = name.includes('restricted');
      this.isACECover = name.includes('ace');
      if (!this.isACECover) {
        this.policyCover.get('is_loss_of_horse_selected').setValue(1, { emitEvent: false });
      }
      const classControl = this.policyCover.get('class_of_use_id');
      const bloodstockControl = this.policyCover.get('bloodstock_id');
      if (this.isBloodstockCover) {
        classControl.clearValidators();
        classControl.setValue(EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID, { emitEvent: false });
        bloodstockControl.setValidators([Validators.required]);
        this.loadBloodstockDropdownOptions();
      } else {
        classControl.setValidators([Validators.required]);
        bloodstockControl.clearValidators();
        this.bloodstockLssSelected = false;
      }
      classControl.updateValueAndValidity({ emitEvent: false });
      bloodstockControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Bloodstock class id – pass to equine/static-data/class-of-use/{class_id} to get dropdown values (backend returns blood_stock rows for this id). */
  static readonly BLOODSTOCK_CLASS_ID = '6cudc37f-b4a7-431e-9436-8d03cecceac0';

  /** Dropdown options when Bloodstock: from API; selected option id is stored in bloodstock_id. */
  bloodstockDropdownOptions: any[] = [];

  /** Load dropdown from equine/static-data/class-of-use/{bloodstock_class_id} (and optionally cover-types); selected id → bloodstock_id. */
  private loadBloodstockDropdownOptions() {
    const classId = EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID;
    this.equineService.getClassActivities(classId).subscribe({
      next: (res: any) => {
        const list = res?.result ?? res?.data ?? [];
        this.bloodstockDropdownOptions = Array.isArray(list) ? list : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.bloodstockDropdownOptions = [];
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * update the available classes to the user 
   * @returns 
   */
  public updateAvailableClasses() {
    if (this.horseAge !== null && this.horseAge < 1) {
      // Horse age < 1: restrict to Class 2 only
      this.classes = (this.staticData?.class_of_use || []).filter(c => {
        return c.class_name === 'Class 2' || c.class === 2;
      });

      const class2 = this.classes[0];
      if (class2 && this.policyCover.get('class_of_use_id').value !== class2.id) {
        this.policyCover.get('class_of_use_id').setValue(class2.id, { emitEvent: false });
        // retrieve activities for class 2
        this.loadClassActivitiesForDisplay(class2.id);
      }
    } else {
      this.loadStaticData();
      if (this.isRestrictedCover) {
        this.classes = this.classes.filter(c => !c.class_name.includes('Bloodstock'));
      }
    }

    // filter out class 4
    if(this.isRestrictedCover) {
      this.classes = this.classes.filter(c => c.class_name !== 'Class 4');
    }
  }

  selectClass(classId: string) {
    const classData = this.classes.find(c => c.id === classId || c.id == classId);
    if (!classData) return;
    if (this.horseAge !== null && this.horseAge < 1 && classData.class !== 2) return;
    if (this.isRestrictedCover && classData.class === 4) return;
    this.policyCover.get('class_of_use_id').setValue(classId);
  }

  isClassAvailable(classNumber: number): boolean {
    if (this.horseAge !== null && this.horseAge < 1) return classNumber === 2;
    if (this.isRestrictedCover && classNumber === 4) return false;
    return true;
  }

  isClassSelected(classId: string): boolean {
    return this.policyCover.get('class_of_use_id').value === classId;
  }

  getClassActivities(classData: any): any[] {
    return classData?.sub_classes || [];
  }

  /**
   * Check if standard cover
   */
  public isStandardCover(): boolean {
    const ct = this.policyCover.get('cover_type_id').value;
    if (!ct) return false;
    const selected = this.coverTypes.find(t => t.id === ct);
    const name = (selected.cover_type_name).toLowerCase();
    return !name.includes('restricted') && !name.includes('ace') && !name.includes('bloodstock');
  }

  public isLOUEligible(): boolean {
    const age = this.horseAge != null ? this.horseAge : this.calculatedAge;
    if (age == null) return false;
    // if horse age is not between 4 and 12, return false
    if (age < 4 || age > 12) return false;
    // get the class of use id
    const couId = this.policyCover.get('class_of_use_id').value;
    if (!couId) return false;
    // get the selected class
    const selectedClass = this.classes.find(c => c.id === couId || c.id == couId);
    // return true if the selected class is between 2 and 4
    return selectedClass && ['Class 2', 'Class 3', 'Class 4'].includes(selectedClass.class_name);
  }

  isLOUNoLocked(): boolean {
    if (this.isLOUEligible()) return false;
    return true;
  }

  public applyCoverDetailsRules(): void {
    if (this.applyingCoverRules || !this.formFields || !this.isStandardCover()) return;
    this.applyingCoverRules = true;
    const noEmit = { emitEvent: false };
    try {
      const louControl = this.policyCover.get('is_cover_for_permanent_lou');
      if (!this.isLOUEligible()) {
        louControl.setValue(0, noEmit);
        louControl.disable(noEmit);
      } else {
        louControl.enable(noEmit);
      }

      const isLou = Number(louControl.value) === 1;
      const louActivityControl = this.policyCover.get('lou_activity_id');
      if (isLou) {
        louActivityControl.setValidators([Validators.required]);
      } else {
        louActivityControl.clearValidators();
        louActivityControl.setValue('', noEmit);
      }
      louActivityControl.updateValueAndValidity(noEmit);
      this.updateLouOtherSpecifyValidators(noEmit);

      if (isLou && Number(this.policyCover.get('is_vet_fee_selected').value) === 1) {
        const wasEssential = Number(this.policyCover.get('vet_fee_cover_type').value) === 2;
        this.policyCover.get('vet_fee_cover_type').setValue(1, noEmit);
        if (wasEssential) {
          this.policyCover.get('vet_fee_id').setValue(null, noEmit);
          this.policyCover.get('vet_fee_excess_id').setValue(null, noEmit);
          this.policyCover.get('vet_fee_co_insurance_id').setValue(null, noEmit);
          this.policyCover.get('vet_fee_essential_excess_id').setValue(null, noEmit);
        }
      } else {
        this.policyCover.get('vet_fee_cover_type').enable(noEmit);
      }

      const vfType = Number(this.policyCover.get('vet_fee_cover_type').value);
      if (vfType === 1) {
        const excessId = this.policyCover.get('vet_fee_excess_id').value;
        if (excessId === this.VET_FEE_EXCESS_150_ID) {
          const coId = this.policyCover.get('vet_fee_co_insurance_id').value;
          if (!this.CO_INSURANCE_IDS_20_OR_MORE.includes(coId)) {
            this.policyCover.get('vet_fee_co_insurance_id').setValue(this.CO_INSURANCE_20_ID, noEmit);
          }
        }
      }

      const sumInsured = Number(this.horseDetails.get('sum_insured').value) || 0;
      const isVetFeeSelected = Number(this.policyCover.get('is_vet_fee_selected').value) === 1;
      const lifeSavingControl = this.policyCover.get('is_life_saving_surgery_selected');
      if (sumInsured > 20000 && isVetFeeSelected) {
        lifeSavingControl.setValue(1, noEmit);
        lifeSavingControl.disable(noEmit);
      } else {
        lifeSavingControl.enable(noEmit);
        if (!isVetFeeSelected) {
          lifeSavingControl.setValue(0, noEmit);
        }
      }
      this.clearUnavailableVetFeeSelection();
    } finally {
      this.applyingCoverRules = false;
    }
  }

  private isLouOtherActivity(activityId: string): boolean {
    const activity = this.louActivities.find(a => a.id === activityId);
    return activity?.name?.toLowerCase() === 'other';
  }

  private updateLouOtherSpecifyValidators(noEmit: { emitEvent: boolean } = { emitEvent: false }): void {
    if (!this.policyCover) return;
    const otherCtrl = this.policyCover.get('lou_other_specify');
    if (!otherCtrl) return;
    const isLou = Number(this.policyCover.get('is_cover_for_permanent_lou').value) === 1;
    const isOther = this.isLouOtherActivity(this.policyCover.get('lou_activity_id').value);
    if (isLou && isOther) {
      otherCtrl.setValidators([Validators.required]);
    } else {
      otherCtrl.clearValidators();
      if (!isOther) {
        otherCtrl.setValue('', noEmit);
      }
    }
    otherCtrl.updateValueAndValidity(noEmit);
  }

  applyLouOtherReferral(): void {
    if (this.applyingLouReferral || !this.formFields) return;
    this.applyingLouReferral = true;
    try {
      const activityId = this.policyCover.get('lou_activity_id').value;
      const otherText = (this.policyCover.get('lou_other_specify').value || '').trim();
      this.requiresLouReferral = this.isLouOtherActivity(activityId) && otherText.length > 0;
    } finally {
      this.applyingLouReferral = false;
    }
  }

  get louActivityIsOther(): boolean {
    return this.isLouOtherActivity(this.policyCover.get('lou_activity_id').value);
  }

  /**
   * Lock enhanced cover when LOU is selected and vet fee is selected 
   */
  get isVetFeeEnhancedLocked(): boolean {
    return Number(this.policyCover.get('is_cover_for_permanent_lou').value) === 1 &&
      Number(this.policyCover.get('is_vet_fee_selected').value) === 1;
  }

  public isVfOptionAvailable(opt: { minSi?: number; maxAge?: number }): boolean {
    if (!opt.minSi && !opt.maxAge) return true;
    const sumInsured = Number(this.horseDetails.get('sum_insured').value) || 0;
    const age = this.horseAge != null ? this.horseAge : this.calculatedAge;
    if (opt.minSi != null && sumInsured <= opt.minSi) return false;
    if (opt.maxAge != null && (age == null || age > opt.maxAge)) return false;
    return true;
  }

  private clearUnavailableVetFeeSelection(): void {
    if (!this.policyCover) return;
    if (Number(this.policyCover.get('is_vet_fee_selected').value) !== 1) return;
    if (Number(this.policyCover.get('vet_fee_cover_type').value) !== 1) return;
    const vetFeeId = this.policyCover.get('vet_fee_id').value;
    if (!vetFeeId) return;
    const opt = this.vfEnhancedOptions.find((o) => o.id === vetFeeId);
    if (opt && !this.isVfOptionAvailable(opt)) {
      this.policyCover.get('vet_fee_id').setValue(null, { emitEvent: false });
    }
  }

  get is150ExcessSelected(): boolean {
    return this.policyCover.get('vet_fee_excess_id').value === this.VET_FEE_EXCESS_150_ID;
  }

  isCoInsuranceDisabled(opt: { value: number }): boolean {
    return this.is150ExcessSelected && (opt.value === 0 || opt.value === 15);
  }

  get isLifeSavingFree(): boolean {
    const sumInsured = Number(this.horseDetails.get('sum_insured').value) || 0;
    const isVetFeeSelected = Number(this.policyCover.get('is_vet_fee_selected').value) === 1;
    return isVetFeeSelected && sumInsured > 20000;
  }

  validateDob() {
    const dob = this.proposerControls.date_of_birth.value;
    const inceptionDate = this.formFields.get('policy_start_date').value;

    if (dob && inceptionDate) {
      const birthDate = new Date(dob);
      const inception = new Date(inceptionDate);
      const age = inception.getFullYear() - birthDate.getFullYear();
      const monthDiff = inception.getMonth() - birthDate.getMonth();

      if (age < 18 || (age === 18 && monthDiff < 0)) {
        this.proposerControls.date_of_birth.setErrors({ 'minAge': true });
      } else {
        const errors = this.proposerControls.date_of_birth.errors;
        if (errors) {
          delete errors['minAge'];
          if (Object.keys(errors).length === 0) {
            this.proposerControls.date_of_birth.setErrors(null);
          } else {
            this.proposerControls.date_of_birth.setErrors(errors);
          }
        }
      }
    }
  }

  /** Returns an error message if vet fee excess / co-insurance are not selected, or null if valid. */
  private validateVetFeeSelections(): string | null {
    const isVetFeeSelected = Number(this.policyCover.get('is_vet_fee_selected').value) === 1;
    if (!isVetFeeSelected) return null;

    const vetFeeCoverType = Number(this.policyCover.get('vet_fee_cover_type').value);
    const excessId = this.policyCover.get('vet_fee_excess_id').value;
    const coInsId = this.policyCover.get('vet_fee_co_insurance_id').value;
    const essentialExcessId = this.policyCover.get('vet_fee_essential_excess_id').value;
    const vetFeeId = this.policyCover.get('vet_fee_id').value;

    if (this.isRestrictedCover) {
      if (!vetFeeId) return 'Please select a Veterinary Fees cover option.';
      if (!excessId) return 'Please select a Veterinary Fees excess option.';
      return null;
    }

    if (this.isACECover || this.isBloodstockCover) {
      return null;
    }

    if (vetFeeCoverType === 1) {
      if (!vetFeeId) return 'Please select a Veterinary Fees cover option.';
      const selectedOpt = this.vfEnhancedOptions.find((o) => o.id === vetFeeId);
      if (selectedOpt && !this.isVfOptionAvailable(selectedOpt)) {
        this.clearUnavailableVetFeeSelection();
        return 'Please select a valid Veterinary Fees cover option. The selected option is not available for this horse age / sum insured.';
      }
      if (!excessId) return 'Please select a Veterinary Fees fixed excess option.';
      if (!coInsId) return 'Please select a Veterinary Fees co-insurance option.';
    } else if (vetFeeCoverType === 2) {
      if (!vetFeeId) return 'Please select a Veterinary Fees cover option.';
      if (!excessId) return 'Please select a Veterinary Fees excess option.';
    }

    return null;
  }

  /** Get policy cover field: backend may return at quote root or under policy_cover_details */
  private getPc(key: string): any {
    const m = this.modelObject;
    return m?.policy_cover_details?.[key] !== undefined ? m.policy_cover_details[key] : m?.[key];
  }

  patchFormValues() {
    this.suppressAutoSave = true;
    try {
    const m = this.modelObject;
    const noEmit = { emitEvent: false };

    this.quoteID = m.id;

    this.formFields.patchValue({
      policy_start_date: m.policy_start_date ? new Date(m.policy_start_date) : null,
      period_of_cover_to: m.policy_end_date ? new Date(m.policy_end_date) : null,
      mta_effective_date: m.effective_date ? new Date(m.effective_date) : '',
      mta_reason_id: m.mta_reason_id || '',
      mta_reason_other: m.mta_reason_other || '',
      broker_id: m.broker_id || '',
      company_id: m.company_id || '',
      transaction_type: m.transaction_type != null ? m.transaction_type : 1
    }, noEmit);

    if (m.proposer_details) {
      const pd = m.proposer_details;
      const addr = pd.address;


      this.formFields.patchValue({
        proposer_details: {
          title: pd.title != null ? pd.title : '',
          forename: pd.first_name != null ? pd.first_name : (pd.forename != null ? pd.forename : ''),
          surname: pd.last_name != null ? pd.last_name : (pd.surname != null ? pd.surname : ''),
          date_of_birth: pd.dob ? new Date(pd.dob) : null,
          email_address: pd.email != null ? pd.email : (pd.email_address != null ? pd.email_address : ''),
          phone_number: pd.phone != null ? pd.phone : (pd.phone_number != null ? pd.phone_number : ''),
          address: {
            postcode: addr.postcode != null ? addr.postcode : '',
            address1: addr.address1 != null ? addr.address1 : '',
            address2: addr.address2 != null ? addr.address2 : '',
            town_county: addr.city != null ? addr.city : '',
          }
        }
      }, noEmit);
    } 

    if (m.horse_details || m.horse_Details) {
      const hd = m.horse_details || m.horse_Details;
      const stableName = hd.stable_name != null ? hd.stable_name : (hd.name != null ? hd.name : '');
      const passportName = hd.passport_name != null ? hd.passport_name : (hd.passport != null ? hd.passport : '');
      let sumInsured = 0;
      if(hd.sum_insured != null) {
        sumInsured = Number(hd.sum_insured);
      } else if(m.sum_insured != null) {
        sumInsured = Number(m.sum_insured);
      }

      let purchasePrice = 0;
      if(hd.purchase_price != null) {
        purchasePrice = Number(hd.purchase_price);
      } else if(m.purchase_price != null) {
        purchasePrice = Number(m.purchase_price);
      }

      const yearOfBirth = hd.horse_birth_year != null ? hd.horse_birth_year : (hd.horse_birth_year != null ? hd.horse_birth_year : null);
      const acquiredDate = hd.acquired_date ? new Date(hd.acquired_date) : (hd.horse_purchase_date ? new Date(hd.horse_purchase_date) : null);
      const ownershipId = hd.ownership_status_id != null ? hd.ownership_status_id : (hd.ownership_status != null ? hd.ownership_status : '');

      this.horseDetails.get('stable_name').setValue(stableName, noEmit);
      this.horseDetails.get('passport_name').setValue(passportName, noEmit);
      this.horseDetails.get('acquired_date').setValue(acquiredDate, noEmit);
      this.horseDetails.get('ownership_status').setValue(ownershipId, noEmit);
      this.horseDetails.get('sum_insured').setValue(sumInsured, noEmit);
      this.horseDetails.get('horse_birth_year').setValue(yearOfBirth, noEmit);
      
      if (hd.age != null && hd.age !== undefined) {
        const ageVal = hd.age === '<1' || hd.age === 0 ? '<1' : Number(hd.age);
        this.horseDetails.get('age').setValue(ageVal, noEmit);
      }
      if (ownershipId) {
        this.onOwnershipStatusChange(true);
      }
      this.horseDetails.get('purchase_price').setValue(purchasePrice, noEmit);
      this.calculateAge();
    }

    const mainUseId = this.getPc('horse_class_sub_id') != null ? this.getPc('horse_class_sub_id') : (this.getPc('sub_class_of_use_id') != null ? this.getPc('sub_class_of_use_id') : this.getPc('main_use_of_horse_id'));
    if (mainUseId) {
      this.policyCover.get('main_use_of_horse_id').setValue(mainUseId, noEmit);
    }

    this.policyCover.get('is_elective_surgery').setValue(this.getPc('is_elective_surgery') != null ? this.getPc('is_elective_surgery') : 0, noEmit);
    this.policyCover.get('is_disposal_euthansia').setValue(this.getPc('is_disposal_euthansia') != null ? this.getPc('is_disposal_euthansia') : 0, noEmit);
    this.policyCover.get('is_cover_for_permanent_lou').setValue(this.getPc('is_cover_for_permanent_lou') != null ? this.getPc('is_cover_for_permanent_lou') : 0, noEmit);
    const louActivityId = this.getPc('lou_activity_id');
    if (louActivityId != null) this.policyCover.get('lou_activity_id').setValue(louActivityId, noEmit);
    const louOther = this.getPc('lou_other_specify');
    if (louOther != null) this.policyCover.get('lou_other_specify').setValue(louOther, noEmit);
    this.policyCover.get('is_vet_fee_selected').setValue(this.getPc('is_vet_fee_selected') != null ? this.getPc('is_vet_fee_selected') : 0, noEmit);
    const vetFeeCoverType = this.getPc('vet_fee_cover_type');
    if (vetFeeCoverType != null && vetFeeCoverType !== '') this.policyCover.get('vet_fee_cover_type').setValue(vetFeeCoverType, noEmit);
    const vetFeeId = this.getPc('vet_fee_id');
    if (vetFeeId) this.policyCover.get('vet_fee_id').setValue(vetFeeId, noEmit);
    const vetFeeExcessId = this.getPc('vet_fee_excess_id');
    if (vetFeeExcessId) this.policyCover.get('vet_fee_excess_id').setValue(vetFeeExcessId, noEmit);
    const vetFeeCoId = this.getPc('vet_fee_co_insurance_id');
    if (vetFeeCoId) this.policyCover.get('vet_fee_co_insurance_id').setValue(vetFeeCoId, noEmit);
    const vetFeeEssentialExcessId = this.getPc('vet_fee_essential_excess_id');
    if (vetFeeEssentialExcessId) this.policyCover.get('vet_fee_essential_excess_id').setValue(vetFeeEssentialExcessId, noEmit);
    this.policyCover.get('is_livery_hispitalization').setValue(this.getPc('is_livery_hispitalization') != null ? this.getPc('is_livery_hispitalization') : 0, noEmit);
    this.policyCover.get('is_life_saving_surgery_selected').setValue(this.getPc('is_life_saving_surgery_selected') != null ? this.getPc('is_life_saving_surgery_selected') : 0, noEmit);
    this.policyCover.get('is_loss_of_horse_selected').setValue(this.getPc('is_loss_of_horse_selected') != null ? this.getPc('is_loss_of_horse_selected') : 0, noEmit);
    const bloodstockId = this.getPc('bloodstock_id') ?? this.getPc('blood_stock_id') ?? (m && (m as any).blood_stock_id);
    if (this.policyCover.get('bloodstock_id') && bloodstockId != null) {
      this.policyCover.get('bloodstock_id').setValue(bloodstockId, noEmit);
    }
    if (!this.isACECover) {
      this.policyCover.get('is_loss_of_horse_selected').setValue(1, noEmit);
    }
    // When loading a bloodstock quote, detect if vet fee was set to £7500/£350 to restore the UI toggle
    if (this.isBloodstockCover) {
      const vetFeeId = this.policyCover.get('vet_fee_id').value;
      const vetFeeExcessId = this.policyCover.get('vet_fee_excess_id').value;
      this.bloodstockLssSelected = (vetFeeId === EquineQuoteDetailsComponent.VET_FEE_COVER['7500_bloodstock']
        && vetFeeExcessId === EquineQuoteDetailsComponent.VET_FEE_EXCESS['350_bloodstock']);
      this.policyCover.get('is_life_saving_surgery_selected').setValue(0, noEmit);
    }
    // When loading an ACE quote, ensure £7500 vet fee and £350 excess are set if missing
    if (this.isACECover) {
      if (!this.policyCover.get('vet_fee_id').value) {
        this.policyCover.get('vet_fee_id').setValue(EquineQuoteDetailsComponent.VET_FEE_COVER['7500_ace'], noEmit);
      }
      if (!this.policyCover.get('vet_fee_excess_id').value) {
        this.policyCover.get('vet_fee_excess_id').setValue(EquineQuoteDetailsComponent.VET_FEE_EXCESS['350_ace'], noEmit);
      }
      this.policyCover.get('is_vet_fee_selected').setValue(1, noEmit);
      this.policyCover.get('vet_fee_cover_type').setValue(1, noEmit);
      // ACE with Loss of Horse No: sum insured not required, default 0
      if (Number(this.policyCover.get('is_loss_of_horse_selected').value) === 2) {
        this.horseDetails.get('sum_insured').setValue(0, noEmit);
        this.horseDetails.get('sum_insured').clearValidators();
        this.horseDetails.get('sum_insured').updateValueAndValidity(noEmit);
      }
    }

    this.applyCoverDetailsRules();
    this.applyLouOtherReferral();
    this.onOwnershipStatusChange(true);
    this.updateMtaReasonOtherValidators();
    } finally {
      this.suppressAutoSave = false;
    }
  }

  /** After first load, keep ownership mat-select in sync when sibling tabs/saves update model (API often uses ownership_status_id only). */
  private syncHorseOwnershipFromModel(): void {
    if (!this.formFields || !this.modelObject) return;
    const hd = this.modelObject.horse_details || this.modelObject.horse_Details;
    if (!hd) return;
    const ownershipId =
      hd.ownership_status_id != null && hd.ownership_status_id !== ''
        ? hd.ownership_status_id
        : (hd.ownership_status != null && hd.ownership_status !== '' ? hd.ownership_status : null);
    if (ownershipId == null || ownershipId === '') return;
    const ctrl = this.horseDetails?.get('ownership_status');
    if (!ctrl) return;
    const cur = ctrl.value;
    if (cur === ownershipId || String(cur) === String(ownershipId)) return;
    ctrl.setValue(ownershipId, { emitEvent: false });
    this.onOwnershipStatusChange(true);
  }

  public getBrokers() {
    this.quoteService.getBrokers(10)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.brokers = response.result;
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  public getPolicyReasons() {
    this.policyService.getPolicyMTAReasons(environment.NEW_EQUINE_ID)
      .subscribe(
        (response: any) => {
          if (response.success) {
            const reasonType = this.isMta ? 1 : 2;
            this.policyReasons = (response.result?.types || []).filter(
              (r: any) =>
                r.product_id === environment.NEW_EQUINE_ID &&
                Number(r.type) === reasonType &&
                Number(r.status) === 1
            );
            this.updateMtaReasonOtherValidators();
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  /**
   * Build save payload from form and modelObject. Safe for partial data; does not validate required fields.
   */
  private buildSavePayload(options?: { forPremiumUpdate?: boolean; forAutoSave?: boolean }): any {

    const forBackgroundSave = options?.forPremiumUpdate === true || options?.forAutoSave === true;
    const underwritingNotesForSave = (this.modelObject?.underwriting_notes || 'Automatic quote');
    const formValue = this.formFields.getRawValue();
    const safeDate = (val: any) => (val ? formatDate(val, 'yyyy-MM-dd', 'en') : null);

    let companyId: string | null;
    if (this.sessionState.retrieve('company_role') === 'Broker') {
      companyId = this.sessionState.retrieve('broker_company_id');
    } else {
      companyId = formValue?.company_id || this.modelObject?.company_id || null;
      if (!companyId) {
        const userInfo = this.sessionState.retrieve('userInfo');
        companyId = userInfo ? userInfo.company_id : null;
      }
    }
    const brokerId = formValue.broker_id || this.modelObject.broker_id || this.sessionState.retrieve('userInfo')?.id || '';

    const pd = formValue?.proposer_details || {};
    const existingPd = this.modelObject?.proposer_details || {};
    const hd = formValue?.horse_details || {};
    const existingHd = this.modelObject?.horse_details || this.modelObject?.horse_Details || {};
    const pc = formValue?.policy_cover_details || {};
    const m = this.modelObject;
    const existingPc = m?.policy_cover_details && Object.keys(m.policy_cover_details).length
      ? m.policy_cover_details
      : {
          cover_type_id: m?.cover_type_id,
          class_of_use_id: m?.horse_class_id,
          sub_class_of_use_id: m?.horse_class_sub_id,
          bloodstock_id: m?.blood_stock_id,
          is_lou_selected: m?.is_lou_selected,
          is_elective_surgery: m?.is_elective_surgery,
          is_disposal_euthansia: m?.is_disposal_euthansia,
          is_cover_for_permanent_lou: m?.is_cover_for_permanent_lou,
          lou_activity_id: m?.lou_activity_id,
          is_vet_fee_selected: m?.is_vet_fee_selected,
          vet_fee_cover_type: m?.vet_fee_cover_type,
          vet_fee_id: m?.vet_fee_id,
          vet_fee_excess_id: m?.vet_fee_excess_id,
          vet_fee_co_insurance_id: m?.vet_fee_co_insurance_id,
          is_livery_hispitalization: m?.is_livery_hispitalization,
          is_life_saving_surgery_selected: m?.is_life_saving_surgery_selected,
        };

    const coverTypeId = pc.cover_type_id !== undefined ? pc.cover_type_id : (existingPc.cover_type_id ?? '');
    const isVetFeeSelectedRaw = pc.is_vet_fee_selected !== undefined ? pc.is_vet_fee_selected : (existingPc.is_vet_fee_selected ?? null);
    const isVetFeeSelected = Number(isVetFeeSelectedRaw ?? 0) === 1;
    let vetFeeCoverType = pc.vet_fee_cover_type !== undefined ? pc.vet_fee_cover_type : (existingPc.vet_fee_cover_type ?? null);
    if (this.isACECover || this.isRestrictedCover) {
      vetFeeCoverType = 0;
    }
    if (isVetFeeSelected && (vetFeeCoverType === '' || vetFeeCoverType == null)) vetFeeCoverType = 1;
    if (vetFeeCoverType !== null && vetFeeCoverType !== '' && vetFeeCoverType !== undefined) {
      vetFeeCoverType = Number(vetFeeCoverType) || 0;
    }
    let vetFeeId = pc.vet_fee_id !== undefined ? pc.vet_fee_id : (existingPc.vet_fee_id ?? null);
    let vetFeeExcessId = pc.vet_fee_excess_id !== undefined ? pc.vet_fee_excess_id : (existingPc.vet_fee_excess_id ?? null);
    const emptyId = (v: any) => v === '' || v == null || (typeof v === 'string' && !v.trim());

    if (isVetFeeSelected && vetFeeCoverType === 1) {
      if (emptyId(vetFeeId) || emptyId(vetFeeExcessId)) {
        if (this.isACECover) {
          vetFeeId = vetFeeId && !emptyId(vetFeeId) ? vetFeeId : EquineQuoteDetailsComponent.VET_FEE_COVER['7500_ace'];
          vetFeeExcessId = vetFeeExcessId && !emptyId(vetFeeExcessId) ? vetFeeExcessId : EquineQuoteDetailsComponent.VET_FEE_EXCESS['350_ace'];
        } else if (this.isRestrictedCover) {
          vetFeeId = vetFeeId && !emptyId(vetFeeId) ? vetFeeId : null;
        } else {
          vetFeeId = vetFeeId && !emptyId(vetFeeId) ? vetFeeId : EquineQuoteDetailsComponent.VET_FEE_COVER[5500];
          if (vetFeeCoverType === 1) {
            vetFeeExcessId = vetFeeExcessId && !emptyId(vetFeeExcessId) ? vetFeeExcessId : EquineQuoteDetailsComponent.VET_FEE_EXCESS[150];
          } else if (vetFeeCoverType === 2) {
            vetFeeExcessId = vetFeeExcessId && !emptyId(vetFeeExcessId) ? vetFeeExcessId : EquineQuoteDetailsComponent.VET_FEE_EXCESS['375_20'];
          }
        }
      }
    }
    const norm = (v: any) => (v === '' || (typeof v === 'string' && !v.trim()) ? null : v);
    let vetFeeCoInsuranceId = norm(
      pc.vet_fee_co_insurance_id !== undefined
        ? pc.vet_fee_co_insurance_id
        : (existingPc.vet_fee_co_insurance_id ?? null)
    );
    if (!isVetFeeSelected || Number(vetFeeCoverType) === 2) {
      vetFeeCoInsuranceId = null;
    }
    const bloodstockId = norm(pc.bloodstock_id !== undefined ? pc.bloodstock_id : (existingPc.bloodstock_id ?? null));

    const isMtaOrCancel = this.quote_type === 3 || this.quote_type === 4;
    const currentId = this.modelObject.id || this.quoteID || null;
    const prevPolicyId = this.modelObject.previous_policy_id || null;
    const isFirstMtaSave = isMtaOrCancel && (!currentId || currentId === prevPolicyId);
    const vulnerabilityMarkerDetails =
      this.modelObject?.vulnerability_marker_details ??
      this.modelObject?.vulnerability_details ??
      this.modelObject?.vulnerability_marker_text ??
      null;
    const vulnerabilityMarker =
      this.modelObject?.vulnerability_marker ??
      this.modelObject?.is_vulnerability_marker ??
      null;
    const quoteIdForAdjustments = currentId || this.quoteID || null;
    const storedPremiumAdjustments = quoteIdForAdjustments
      ? this.sessionState.retrieve(`equine_quote_adjustments_${quoteIdForAdjustments}`)
      : null;
    const premiumAdjustments = storedPremiumAdjustments ?? null;
    const resolvedHorseOwnership =
      hd.ownership_status ??
      hd.ownership_status_id ??
      existingHd.ownership_status ??
      existingHd.ownership_status_id ??
      '';

    return {
      id: (isFirstMtaSave || this.isCancellation) ? null : currentId,
      quote_meta_id: this.modelObject.quote_meta_id || null,
      broker_id: brokerId,
      company_id: companyId || '',
      transaction_type: this.isCancellation
        ? 4
        : (formValue.transaction_type || this.modelObject.transaction_type || 1),
      policy_start_date: safeDate(formValue.policy_start_date),
      policy_end_date: safeDate(formValue.period_of_cover_to),
      effective_date: ((this.quote_type === 3 || this.quote_type === 4) && formValue.mta_effective_date) ? safeDate(formValue.mta_effective_date) : null,
      mta_reason_id: formValue.mta_reason_id || this.modelObject.mta_reason_id || null,
      mta_reason_other: this.reasonIsOther ? ((formValue.mta_reason_other || '').trim() || null) : null,
      rating_version: this.modelObject.rating_version || 1,
      legacy_policy_reference: formValue.legacy_policy_reference || this.modelObject.legacy_policy_reference || '',
      insurance_status_id: this.modelObject.insurance_status_id,
      is_manually_referred: forBackgroundSave ? 0 : (this.modelObject.is_manually_referred ?? 0),
      manually_referred_reason: this.modelObject.manually_referred_reason,
      premium_adjustments: premiumAdjustments,
      underwriting_notes: forBackgroundSave ? underwritingNotesForSave : (this.modelObject.underwriting_notes || null),
      append_underwriting_note: forBackgroundSave ? false : true,
      previous_policy_id: resolveEquinePreviousPolicyId(this.modelObject),
      insurer: this.modelObject.insurer || '',
      vulnerability_marker: vulnerabilityMarker,
      vulnerability_marker_details: vulnerabilityMarkerDetails,
      has_form_changed: this.formChangedByUser || !this.formFields.pristine,
      proposer_details: {
        id: isFirstMtaSave ? null : (existingPd.id || null),
        title: pd.title ?? existingPd.title ?? '',
        first_name: pd.forename ?? existingPd.first_name ?? '',
        last_name: pd.surname ?? existingPd.last_name ?? '',
        phone: pd.phone_number ?? existingPd.phone ?? '',
        email: pd.email_address ?? existingPd.email ?? '',
        dob: safeDate(pd.date_of_birth),
        address: {
          id: isFirstMtaSave ? null : (existingPd.address?.id || null),
          address1: pd.address?.address1 ?? existingPd.address?.address1 ?? '',
          address2: pd.address?.address2 ?? existingPd.address?.address2 ?? '',
          address3: existingPd.address?.address3 ?? null,
          address4: existingPd.address?.address4 ?? null,
          city: pd.address?.town_county?.split(',')[0]?.trim() ?? existingPd.address?.city ?? '',
          country: pd.address?.country ?? 'England',
          postcode: pd.address?.postcode ?? existingPd.address?.postcode ?? ''
        }
      },
      horse_details: {
        ...existingHd,
        id: isFirstMtaSave ? null : existingHd.id,
        horse_purchase_date: undefined,
        stable_name: hd.stable_name ?? existingHd.stable_name ?? '',
        passport_name: hd.passport_name ?? existingHd.passport_name ?? '',
        acquired_date: hd.acquired_date ? safeDate(hd.acquired_date) : (existingHd.acquired_date || (existingHd.horse_purchase_date ? safeDate(existingHd.horse_purchase_date) : null)),
        ownership_status: resolvedHorseOwnership,
        ownership_status_id: resolvedHorseOwnership,
        purchase_price: hd.purchase_price ?? existingHd.purchase_price ?? existingHd.value ?? '',
        sum_insured: hd.sum_insured ?? existingHd.sum_insured ?? '',
        horse_birth_year: hd.horse_birth_year ?? existingHd.horse_birth_year ?? existingHd.birth_year ?? null,
        age: hd.age ?? existingHd.age ?? null
      },
      policy_cover_details: {
        cover_type_id: coverTypeId,
        class_of_use_id: this.isBloodstockCover
          ? EquineQuoteDetailsComponent.BLOODSTOCK_CLASS_ID
          : norm(pc.class_of_use_id !== undefined ? pc.class_of_use_id : (existingPc.class_of_use_id ?? null)),
        sub_class_of_use_id: norm(pc.main_use_of_horse_id !== undefined ? pc.main_use_of_horse_id : (existingPc.sub_class_of_use_id ?? null)),
        bloodstock_id: bloodstockId,
        is_lou_selected: this.isBloodstockCover
          ? false
          : norm(pc.is_cover_for_permanent_lou !== undefined ? pc.is_cover_for_permanent_lou : (existingPc.is_cover_for_permanent_lou ?? null)),
        cover_sub_types_id: pc.cover_sub_types_id !== undefined ? pc.cover_sub_types_id : (existingPc.cover_sub_types_id ?? ''),
        is_elective_surgery: norm(pc.is_elective_surgery !== undefined ? pc.is_elective_surgery : (existingPc.is_elective_surgery ?? null)),
        is_disposal_euthansia: norm(pc.is_disposal_euthansia !== undefined ? pc.is_disposal_euthansia : (existingPc.is_disposal_euthansia ?? null)),
        is_cover_for_permanent_lou: norm(pc.is_cover_for_permanent_lou !== undefined ? pc.is_cover_for_permanent_lou : (existingPc.is_cover_for_permanent_lou ?? null)),
        lou_activity_id: norm(pc.lou_activity_id !== undefined ? pc.lou_activity_id : (existingPc.lou_activity_id ?? null)),
        lou_other_specify: pc.lou_other_specify !== undefined ? pc.lou_other_specify : (existingPc.lou_other_specify ?? null),
        is_vet_fee_selected: norm(isVetFeeSelectedRaw),
        vet_fee_cover_type: vetFeeCoverType == null ? null : vetFeeCoverType,
        vet_fee_id: norm(vetFeeId),
        vet_fee_excess_id: norm(vetFeeExcessId),
        vet_fee_co_insurance_id: vetFeeCoInsuranceId,
        vet_fee_essential_excess_id: norm(pc.vet_fee_essential_excess_id !== undefined ? pc.vet_fee_essential_excess_id : (existingPc.vet_fee_essential_excess_id ?? null)),
        is_livery_hispitalization: norm(pc.is_livery_hispitalization !== undefined ? pc.is_livery_hispitalization : (existingPc.is_livery_hispitalization ?? null)),
        is_life_saving_surgery_selected: norm(pc.is_life_saving_surgery_selected !== undefined ? pc.is_life_saving_surgery_selected : (existingPc.is_life_saving_surgery_selected ?? null)),
        is_loss_of_horse_selected: norm(pc.is_loss_of_horse_selected !== undefined ? pc.is_loss_of_horse_selected : (existingPc.is_loss_of_horse_selected ?? null))
      },
      statement_of_facts: isFirstMtaSave
        ? (this.modelObject.statement_of_facts || []).map((s: any) => ({ ...s, id: null, quote_id: null }))
        : (this.modelObject.statement_of_facts || [])
    };
  }

  private performAutoSave(): void {
    if (!this.formFields) return;
    if (!this.canAutoSaveQuote()) return;
    if (this.autoSaveInFlight) {
      this.pendingAutoSave = true;
      return;
    }

    const data = this.buildSavePayload({ forAutoSave: true });
    const prevPolicyId = this.modelObject.previous_policy_id;
    const transactionType = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType = this.modelObject.original_transaction_type;
    this.autoSaveInFlight = true;
    this.equineService.saveQuote(data).subscribe({
      next: (response: any) => {
        if (response.success && response.result) {
          const quoteId = response.id ?? response.result?.id;
          if (quoteId) {
            this.quoteID = quoteId;
            response.result.id = quoteId;
            const snapshot = getEquineDisplayPremiumData({
              ...response.result,
              type: transactionType,
              transaction_type: transactionType,
              previous_policy_id: prevPolicyId,
            });
            if (snapshot) {
              this.sessionState.store(`equine_quote_adjusted_premium_${quoteId}`, snapshot);
            }
          }
          this.modelObject = normalizeEquineQuotedReferralState(response.result);
          if (prevPolicyId) {
            this.modelObject.previous_policy_id = prevPolicyId;
          }
          if (transactionType) {
            this.modelObject.type = transactionType;
          }
          if (origTxType) {
            this.modelObject.original_transaction_type = origTxType;
          }
          this.skipPatchFromPremiumUpdate = true;
          this.modelObjectUpdated.emit(this.modelObject);
        }
      },
      complete: () => {
        this.autoSaveInFlight = false;
        if (this.pendingAutoSave) {
          this.pendingAutoSave = false;
          this.performAutoSave();
        }
      },
      error: () => {
        this.autoSaveInFlight = false;
        if (this.pendingAutoSave) {
          this.pendingAutoSave = false;
          this.performAutoSave();
        }
      },
    });
  }

  /**
   * Submit data to backend 
   * @returns 
   */
  public onSubmit(): void {
    this.submitted = true;
    
    if (this.isBloodstockCover) {
      this.policyCover.get('class_of_use_id').clearValidators();
      this.policyCover.get('class_of_use_id').updateValueAndValidity({ emitEvent: false });
      this.policyCover.get('main_use_of_horse_id').clearValidators();
      this.policyCover.get('main_use_of_horse_id').updateValueAndValidity({ emitEvent: false });
    }

    if(this.policyCover.get('is_cover_for_permanent_lou').value === 1 && this.policyCover.get('lou_activity_id').value === '') {
      this.policyCover.get('lou_activity_id').setErrors({ required: true });
      this.policyCover.get('lou_activity_id').markAsTouched();

      scrollToFirstInvalidControl(this.policyCover, this.el.nativeElement);
      return;
    }

    this.updateLouOtherSpecifyValidators({ emitEvent: false });
    const louOtherCtrl = this.policyCover.get('lou_other_specify');
    if (
      Number(this.policyCover.get('is_cover_for_permanent_lou').value) === 1
      && this.louActivityIsOther
      && !(louOtherCtrl?.value || '').toString().trim()
    ) {
      louOtherCtrl.setErrors({ required: true });
      louOtherCtrl.markAsTouched();
      scrollToFirstInvalidControl(this.policyCover, this.el.nativeElement);
      return;
    }

    // if essential is selected and enhanced is locked, 
    if (this.isVetFeeEnhancedLocked && this.policyCover.get('vet_fee_cover_type').value === 2) {
      this.policyCover.get('vet_fee_cover_type').setErrors({ required: true });
      this.policyCover.get('vet_fee_cover_type').markAsTouched();
      this._snackBar.open('Because LOU cover has been selected, only Enhanced cover is available', 'Close', { duration: 5000 });

      return;
    }

    const vetFeeValidationError = this.validateVetFeeSelections();
    if (vetFeeValidationError) {
      this._snackBar.open(vetFeeValidationError, 'Close', { duration: 5000 });
      return;
    }

    const inceptionCtrl = this.formFields.get('policy_start_date');
    const inceptionVal = inceptionCtrl?.value;
    if (this.isBroker && inceptionVal) {
      const inceptionDay = new Date(inceptionVal);
      inceptionDay.setHours(0, 0, 0, 0);
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      if (inceptionDay < todayStart) {
        inceptionCtrl.setErrors({ ...(inceptionCtrl.errors || {}), inceptionInPast: true });
        inceptionCtrl.markAsTouched();
        this._snackBar.open(
          'Period of Cover cannot be in the past. Choose today or a future date.',
          'Close',
          { duration: 5000 }
        );
        scrollToFirstInvalidControl(this.formFields, this.el.nativeElement);
        return;
      }
    }
    if (inceptionCtrl?.errors?.['inceptionInPast']) {
      const rest = { ...inceptionCtrl.errors };
      delete rest['inceptionInPast'];
      inceptionCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    if (this.formFields.invalid) {
      this.formFields.markAllAsTouched();
      scrollToFirstInvalidControl(this.formFields, this.el.nativeElement);
      return;
    }

    this.validateDob();
    if (this.proposerControls.date_of_birth?.errors) {
      return;
    }

    const sumInsured = Number(this.horseDetails.get('sum_insured').value) || 0;

    if(!this.isACECover && sumInsured <= 0) {
      this.horseDetails.get('sum_insured').setErrors({ required: true });
      this.horseDetails.get('sum_insured').markAsTouched();
      scrollToFirstInvalidControl(this.horseDetails, this.el.nativeElement);
      return;
    }

    const data = this.buildSavePayload();
    const prevPolicyId = this.modelObject.previous_policy_id;
    const transactionType = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType = this.modelObject.original_transaction_type;

    this.saving = true;
    this.equineService.saveQuote(data).subscribe({
      next: (response: any) => {
        this.saving = false;
        if (response.success) {
          if (this.quote_type === 4) {  
            this._snackBar.open(this.cancellationSuccessMessage, 'Close', { duration: 3000 });
            this.router.navigateByUrl('/policy-list');
            return;
          }
          this._snackBar.open('Quote details saved successfully', 'Close', {
            duration: 3000
          });
          const quoteId = response.id ?? response.result?.id;
          if (quoteId) {
            this.quoteID = quoteId;
            response.result.id = quoteId;
            const snapshot = getEquineDisplayPremiumData({
              ...response.result,
              type: transactionType,
              transaction_type: transactionType,
              previous_policy_id: prevPolicyId,
            });
            if (snapshot) {
              this.sessionState.store(`equine_quote_adjusted_premium_${quoteId}`, snapshot);
            }
          }
          this.skipPatchFromPremiumUpdate = true;
          this.formChangedByUser = false;
          this.modelObject = response.result;
          if (prevPolicyId) {
            this.modelObject.previous_policy_id = prevPolicyId;
          }
          if (transactionType) {
            this.modelObject.type = transactionType;
          }
          if (origTxType) {
            this.modelObject.original_transaction_type = origTxType;
          }
          this.submitted = false;
          this.modelObjectUpdated.emit(this.modelObject);
          this.GoToNextTab.emit();
          
        }
      },
      error: (error) => {
        this.saving = false;
        this._snackBar.open('Error saving quote details', 'Close', {
          duration: 3000
        });
        this.submitted = false;
      }
    });
  }

  /**
   * Get postcodes from product based configuration service
   * @param compnay_id 
   * @param product_id 
   */
  getPostcodes(compnay_id: any, product_id: any) {
    this.productBasedConfigurationService.getPostcodes(compnay_id, product_id)
      .subscribe(
        (response) => {
          const raw = (response as any).result.postcodes;
          // example raw: "IM, JE, GY, HH"

          if (raw && typeof raw === 'string') {
            this.excludedPrefixes = raw
              .split(',')
              .map(x => x.trim().toUpperCase())
              .filter(x => x.length > 0);
          } else if (Array.isArray(raw)) {
            // safety: if backend ever sends array
            this.excludedPrefixes = raw
              .map(x => String(x).trim().toUpperCase())
              .filter(x => x.length > 0);
          } else {
            this.excludedPrefixes = [];
          }
        },
        (error) => {
          this.excludedPrefixes = [];
        }
      );
  }

  /** True when ACE cover is selected and Loss of Horse is No (sum insured not required). */
  get isAceWithLossOfHorseNo(): boolean {
    return this.isACECover && Number(this.policyCover?.get('is_loss_of_horse_selected')?.value) === 2;
  }

  /** When ACE cover: Loss of Horse No → sum insured 0 and not required; Loss of Horse Yes → sum insured required (restore previous value). */
  private onAceLossOfHorseChange(value: number): void {
    if (!this.isACECover) return;
    const sumInsuredControl = this.horseDetails?.get('sum_insured');
    if (!sumInsuredControl) return;
    if (value === 2) {
      const current = Number(sumInsuredControl.value) || 0;
      if (current > 0) {
        this.aceLossOfHorsePreviousSumInsured = current;
      }
      sumInsuredControl.setValue(0, { emitEvent: false });
      sumInsuredControl.clearValidators();
      sumInsuredControl.updateValueAndValidity({ emitEvent: false });
      this.policyCover.get('is_elective_surgery').setValue(0, { emitEvent: false });
      this.policyCover.get('is_disposal_euthansia').setValue(0, { emitEvent: false });
      sumInsuredControl.disable();
      this.openAceNoSumInsuredModal();
    } else {
      sumInsuredControl.setValidators([Validators.required, this.wholeNumberValidator]);
      if (this.aceLossOfHorsePreviousSumInsured != null && this.aceLossOfHorsePreviousSumInsured > 0) {
        sumInsuredControl.setValue(this.aceLossOfHorsePreviousSumInsured, { emitEvent: false });
      }
      sumInsuredControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  openAceNoSumInsuredModal(): void {
    if (this.aceNoSumInsuredModalRef) {
      this.aceNoSumInsuredModalInstance = this.modalService.open(this.aceNoSumInsuredModalRef, { centered: true });
    }
  }

  dismissAceNoSumInsuredModal(): void {
    if (this.aceNoSumInsuredModalInstance) {
      this.aceNoSumInsuredModalInstance.close();
      this.aceNoSumInsuredModalInstance = null;
    }
  }

  /**
   * When vet fee cover type changes, set the vet fee id and excess id
   * @param type - The new vet fee cover type
   */
  public onVetFeeCoverTypeChange(type:number): void{ 
    // 2 = Essential
    // 1 = Enhanced
    if(type === 2 && !this.isVetFeeEnhancedLocked){
      this.policyCover.get('vet_fee_cover_type').setValue(2, { emitEvent: false });

      this.policyCover.get('vet_fee_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_excess_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_co_insurance_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_essential_excess_id').setValue(null, { emitEvent: false });
      this.policyCover.get('is_livery_hispitalization').setValue(0, { emitEvent: false });
      this.policyCover.get('is_life_saving_surgery_selected').setValue(0, {emitEvent: false});
      this.policyCover.get('is_loss_of_horse_selected').setValue(null, {emitEvent: false});

      this.policyCover.get('vet_fee_id').setValue(EquineQuoteDetailsComponent.VET_FEE_COVER[5500], { emitEvent: false });

      this.applyCoverDetailsRules();

      //call requestPremium to update the premium
      this.requestAutoSave();
    }else if(type === 1){
      this.policyCover.get('vet_fee_cover_type').setValue(1, { emitEvent: false });

      this.policyCover.get('vet_fee_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_excess_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_co_insurance_id').setValue(null, { emitEvent: false });
      this.policyCover.get('vet_fee_essential_excess_id').setValue(null, { emitEvent: false });
      this.policyCover.get('is_livery_hispitalization').setValue(0, { emitEvent: false });
      this.policyCover.get('is_life_saving_surgery_selected').setValue(0, {emitEvent: false});
      this.policyCover.get('is_loss_of_horse_selected').setValue(null, {emitEvent: false});

      //call requestPremium to update the premium
      this.requestAutoSave();
    }


    return;
  }

  disableScroll(event: WheelEvent) { 
    event.preventDefault();
  }
}
